/* ═══════════════════════════════════════════════════════════════
   Fotoserie: das Rundum-Etikett aus einigen Fotos statt aus einem Video

   Am Beispiel (Château Phélan Ségur 2010) war die Schrift aus vier
   Fotos deutlich schärfer als aus dem Video: Ein Foto ist nicht
   verwischt, nicht als Video komprimiert und höher aufgelöst. Dafür
   sieht jedes Foto nur eine Seite, und die Stücke müssen genau
   zusammenpassen. Der Weg:

   1. **Flasche finden** (je Foto, verkleinert), ohne Annahme über
      Lage, Größe, Neigung und Form: lange, fast senkrechte Geraden
      (Hough), dann das Paar, das über die meisten Zeilen beidseitig
      belegt ist, die Gestalt einer Flasche hat und dessen Achse durch
      die Halsmitte geht. Nur wenn das scheitert, wie beim Video mit
      kern/flaschenlage.js an mehreren Stellen der Grundform.
   2. **Umriss in voller Auflösung**: Bauchkanten als Geraden (RANSAC
      über alle Kanten je Zeile); welches Paar der Umriss ist, entscheidet
      die Symmetrie zur Halsmitte. Dann je Seite der beste durchgehende
      Pfad von der Mündung bis unter den Boden.
   3. **Entzerren**: Der Bauch ist ein Zylinder. Laufen seine Kanten im
      Bild zusammen, war die Kamera geneigt (am Beispiel 7–10° nach
      unten); die Drehung, die sie parallel und senkrecht stellt und
      die Flasche in die Bildmitte holt, ist die Entzerrung (eine
      Homographie, reine Kameradrehung). Danach blickt eine waagerechte
      Kamera aus dem Abstand D auf die Achse.
   4. **Gestalt mit Perspektive**: Der Umriss eines Kreises vom Radius
      r erscheint halb so breit wie w = f·r / √(D² − r²); daraus
      k = r/D = w / √(f² + w²) je Zeile. Die Zeile des Umrisses gehört
      zu den Randpunkten (Tiefe D(1 − k²)), daraus die Höhe. Gemessen
      wird in Bauchradien ab der Oberkante (s) — so passen die Fotos
      zusammen, auch wenn sie aus verschiedenem Abstand stammen. Die
      Schulter folgt der Grundform (dort läuft der Umriss fast
      waagerecht und ist zeilenweise nicht zu messen). Passt die
      gemessene Höhe deutlich besser zu einer anderen Grundform als der
      gewählten, wird mit dieser noch einmal gemessen (`formSchluessel`
      im Ergebnis).
   5. **Abbildung**: Ein Punkt der Flasche (Höhe s, Winkel ψ zur
      Kamera) liegt im entzerrten Bild bei
        x = cx + f·r·sin ψ / Z,  y = cy + f·h / Z,  Z = 1 − r·cos ψ.
      Mit der Perspektive biegen sich waagerechte Etikettkanten im
      Foto; in der Abwicklung sind sie wieder gerade.
   6. **Fotos zueinander**: Jedes Foto wird grob abgewickelt; markante
      Punkte (Ecken nach Harris) werden über kleine, in der Helligkeit
      genormte Ausschnitte zwischen allen Fotopaaren verglichen. Je Paar
      bis zu drei Gruppen gleicher Verschiebung; welche stimmen, zeigt
      das Gesamtbild (rundum, stimmige Paare, Nachbarn mit Gemeinsamem).
      Die Reihenfolge der Fotos spielt keine Rolle; die Verschiebungen
      dürfen rundum um ein paar Prozent zu groß oder klein sein (die
      grobe Abwicklung kennt die Gestalt noch nicht genau). Teile ohne
      gemeinsame Stelle (blankes Glas zwischen den Etiketten) werden so
      gesetzt, dass die Fotos rundum möglichst gleichmäßig liegen. Danach wird
      jedes passende Paar im Überlappungsbereich dicht nachgemessen
      (Korrelation auf einem Raster) — auch Nachbarn ohne eigene
      Gruppe, an der vorhergesagten Stelle, damit sich die Runde
      schließt.
   7. **Ausgleich**: Je Foto Bauchradius, Oberkante, Drehwinkel und
      eine kleine Kippung der Entzerrung (die Neigung aus dem
      Zusammenlaufen der Bauchkanten ist bei schmalen Flaschen nur auf
      einige Grad genau), dazu die Brennweite (eng an EXIF), so dass
      alle Treffer auf denselben Punkt der Flasche fallen
      (Levenberg-Marquardt, robust gewichtet, danach ohne Ausreißer).
      Am Beispiel (4 Fotos) im Median gut 1 Pixel bei 200 Pixeln je
      Bauchradius, unter 0,3 mm; die Winkel lagen innerhalb von 1° am
      Prototyp mit SIFT. Ohne die Kippung standen die Kapselränder an
      den Nähten bis zu 1 mm versetzt.
   8. **Zusammensetzen**: Zwischen zwei benachbarten Fotos verläuft die
      Naht dort, wo sich die beiden am wenigsten unterscheiden (Pfad
      von oben nach unten, Struktur statt Helligkeit). Die Helligkeit
      wird entlang jeder Naht angeglichen und quer über jedes Foto
      verteilt — sonst zeigt jedes Foto seine Licht- und Schattenseite.
      Vorderseite ist die Mitte des ersten Fotos (die Reihenfolge legt
      die Oberfläche fest, Fotos lassen sich dort verschieben).

   Grenzen: Spiegelungen auf dem Glas bringt jedes Foto mit; je
   weniger Fotos, desto schräger der Blick an den Nähten (bei 4 Fotos
   nur noch 60–70 % der Auflösung, bei 6 über 85 %).

   Reine Logik ohne DOM: Fotos sind { data, width, height } (RGBA),
   dazu wahlweise `exif` (aus kern/exif.js). Tests in
   tests/fotoserie.test.mjs.
   ═══════════════════════════════════════════════════════════════ */

import { FLASCHENFORMEN, FORM_REIHE, formVon, koerperRadius } from "./flasche.js";
import { lageSuchen, luminanz, oberkanteSuchen, profilAusForm, robusteGerade } from "./flaschenlage.js";
import { ruhigsteSpalte, vorderseite } from "./abwicklung.js";
import { brennweitePixel } from "./exif.js";

const ARBEITS_KANTE = 2400;        /* lange Kante, auf die jedes Foto verkleinert wird */
const TEXTUR_MAX = 2048;           /* höchstens so viele Zeilen hat das Ergebnis */
const ABGLEICH_R = 110;            /* Pixel je Bauchradius beim Vergleichen der Fotos */
const PSI_MAX = 1.3;               /* so weit zur Seite (rad) wird verglichen */
const DS = 0.02;                   /* Raster des Profils (Bauchradien) */
const T = 200;                     /* Maßstab der Restfehler: Pixel je Bauchradius */

const warte = () => new Promise((ok) => setTimeout(ok, 0));

const median = (liste) => {
  const s = liste.filter(Number.isFinite).sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : NaN;
};


/* ── Bildwerkzeuge ─────────────────────────────────────────── */

/* Flächengewichte für das Verkleinern von nQ auf nZ Punkte */
function gewichte(nQ, nZ) {
  const s = nQ / nZ, liste = [];
  for (let i = 0; i < nZ; i++) {
    const a = i * s, b = (i + 1) * s, idx = [], w = [];
    for (let k = Math.floor(a); k < Math.ceil(b); k++) {
      const g = Math.min(b, k + 1) - Math.max(a, k);
      if (g > 1e-6) { idx.push(Math.min(nQ - 1, k)); w.push(g / s); }
    }
    liste.push([idx, w]);
  }
  return liste;
}

/** RGBA verkleinern (Flächenmittel), so dass die lange Kante höchstens
    `kante` Pixel misst. */
function verkleinern(bild, kante) {
  const { data, width: W, height: H } = bild;
  const f = kante / Math.max(W, H);
  if (f >= 1) return bild;
  const w = Math.max(1, Math.round(W * f)), h = Math.max(1, Math.round(H * f));
  const gx = gewichte(W, w), gy = gewichte(H, h);
  const zw = new Float32Array(H * w * 3);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < w; x++) {
      const [idx, wt] = gx[x];
      let r = 0, g = 0, b = 0;
      for (let k = 0; k < idx.length; k++) { const o = (y * W + idx[k]) * 4; r += data[o] * wt[k]; g += data[o + 1] * wt[k]; b += data[o + 2] * wt[k]; }
      const z = (y * w + x) * 3; zw[z] = r; zw[z + 1] = g; zw[z + 2] = b;
    }
  }
  const aus = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const [idx, wt] = gy[y];
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0;
      for (let k = 0; k < idx.length; k++) { const z = (idx[k] * w + x) * 3; r += zw[z] * wt[k]; g += zw[z + 1] * wt[k]; b += zw[z + 2] * wt[k]; }
      const o = (y * w + x) * 4; aus[o] = r; aus[o + 1] = g; aus[o + 2] = b; aus[o + 3] = 255;
    }
  }
  return { data: aus, width: w, height: h };
}

/* Gaußfilter, getrennt nach Richtungen, Ränder fortgesetzt. Beide
   Richtungen zeilenweise aufsummiert (eine Zeile mit Rand, ein
   Zeilenpuffer): So läuft der Speicher der Reihe nach durch, was bei
   Bildern mit Millionen Pixeln ein Vielfaches schneller ist. */
function gauss(src, W, H, sigma) {
  if (sigma >= 3) return kastenGauss(src, W, H, sigma);
  const r = Math.max(1, Math.ceil(3 * sigma)), n = 2 * r + 1;
  const k = new Float64Array(n);
  let s = 0;
  for (let i = -r; i <= r; i++) { k[i + r] = Math.exp(-0.5 * (i / sigma) ** 2); s += k[i + r]; }
  for (let i = 0; i < n; i++) k[i] /= s;
  const zw = new Float32Array(W * H), aus = new Float32Array(W * H);
  const zeile = new Float64Array(W + 2 * r), akk = new Float64Array(W);
  for (let y = 0; y < H; y++) {
    const o = y * W;
    for (let x = 0; x < r; x++) { zeile[x] = src[o]; zeile[W + r + x] = src[o + W - 1]; }
    for (let x = 0; x < W; x++) zeile[x + r] = src[o + x];
    akk.fill(0);
    for (let i = 0; i < n; i++) { const w = k[i]; for (let x = 0; x < W; x++) akk[x] += zeile[x + i] * w; }
    for (let x = 0; x < W; x++) zw[o + x] = akk[x];
  }
  for (let y = 0; y < H; y++) {
    akk.fill(0);
    for (let i = -r; i <= r; i++) {
      const q = (y + i < 0 ? 0 : y + i >= H ? H - 1 : y + i) * W, w = k[i + r];
      for (let x = 0; x < W; x++) akk[x] += zw[q + x] * w;
    }
    aus.set(akk, y * W);
  }
  return aus;
}

/* Breite Glättung (σ ≥ 3, nur für örtliche Mittel und Streuungen):
   dreimal ein Kastenfilter, zusammen fast ein Gauß — mit laufenden
   Summen, gleich teuer bei jedem σ. Kastenbreiten nach Kovesi. */
function kastenGauss(src, W, H, sigma) {
  const ideal = Math.sqrt(12 * sigma * sigma / 3 + 1);
  let wl = Math.floor(ideal); if (wl % 2 === 0) wl--;
  const m = Math.round((12 * sigma * sigma - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4));
  let a = Float32Array.from(src), b = new Float32Array(W * H);
  for (let pass = 0; pass < 3; pass++) {
    const r = ((pass < m ? wl : wl + 2) - 1) / 2;
    kastenZeilen(a, b, W, H, r);
    kastenSpalten(b, a, W, H, r);
  }
  return a;
}

function kastenZeilen(von, nach, W, H, r) {
  const n = 2 * r + 1;
  for (let y = 0; y < H; y++) {
    const o = y * W, erster = von[o], letzter = von[o + W - 1];
    const bei = (x) => (x < 0 ? erster : x >= W ? letzter : von[o + x]);
    let s = 0;
    for (let x = -r; x <= r; x++) s += bei(x);
    for (let x = 0; x < W; x++) {
      nach[o + x] = s / n;
      s += bei(x + r + 1) - bei(x - r);
    }
  }
}

function kastenSpalten(von, nach, W, H, r) {
  const n = 2 * r + 1, s = new Float64Array(W);
  const zeile = (y) => (y < 0 ? 0 : y >= H ? H - 1 : y) * W;
  for (let y = -r; y <= r; y++) { const q = zeile(y); for (let x = 0; x < W; x++) s[x] += von[q + x]; }
  for (let y = 0; y < H; y++) {
    const o = y * W, rein = zeile(y + r + 1), raus = zeile(y - r);
    for (let x = 0; x < W; x++) { nach[o + x] = s[x] / n; s[x] += von[rein + x] - von[raus + x]; }
  }
}

/* Bilinear aus einem Float32-Bild */
function bilinear(g, W, H, x, y) {
  if (!(x >= 0 && y >= 0 && x <= W - 1 && y <= H - 1)) return NaN;
  const x0 = Math.min(W - 2, Math.floor(x)), y0 = Math.min(H - 2, Math.floor(y));
  const fx = x - x0, fy = y - y0, o = y0 * W + x0;
  return (g[o] * (1 - fx) + g[o + 1] * fx) * (1 - fy) + (g[o + W] * (1 - fx) + g[o + W + 1] * fx) * fy;
}

/* Bikubisch (Catmull-Rom) aus RGBA in drei Kanäle von `aus` ab `z` */
function bikubisch(d, W, H, x, y, aus, z) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const wx = cr(fx), wy = cr(fy);
  let r = 0, g = 0, b = 0;
  for (let j = 0; j < 4; j++) {
    const yy = Math.min(H - 1, Math.max(0, yi - 1 + j));
    for (let i = 0; i < 4; i++) {
      const xx = Math.min(W - 1, Math.max(0, xi - 1 + i));
      const w = wx[i] * wy[j], o = (yy * W + xx) * 4;
      r += d[o] * w; g += d[o + 1] * w; b += d[o + 2] * w;
    }
  }
  aus[z] = r; aus[z + 1] = g; aus[z + 2] = b;
}
const CR = [0, 0, 0, 0];
function cr(t) {
  const t2 = t * t, t3 = t2 * t;
  CR[0] = -0.5 * t3 + t2 - 0.5 * t;
  CR[1] = 1.5 * t3 - 2.5 * t2 + 1;
  CR[2] = -1.5 * t3 + 2 * t2 + 0.5 * t;
  CR[3] = 0.5 * t3 - 0.5 * t2;
  return [CR[0], CR[1], CR[2], CR[3]];
}


/* ── 3 × 3-Rechnung ────────────────────────────────────────── */

const mal = (A, B) => [0, 1, 2].map((i) => [0, 1, 2].map((j) => A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j]));
const transp = (A) => [0, 1, 2].map((i) => [0, 1, 2].map((j) => A[j][i]));
const kMat = (f, cx, cy) => [[f, 0, cx], [0, f, cy], [0, 0, 1]];
const kInv = (f, cx, cy) => [[1 / f, 0, -cx / f], [0, 1 / f, -cy / f], [0, 0, 1]];
const anwenden = (M, x, y) => {
  const w = M[2][0] * x + M[2][1] * y + M[2][2];
  return [(M[0][0] * x + M[0][1] * y + M[0][2]) / w, (M[1][0] * x + M[1][1] * y + M[1][2]) / w];
};

/* Drehung, die den Einheitsvektor a auf b bringt (Rodrigues) */
function drehungAuf(a, b) {
  const la = Math.hypot(...a), lb = Math.hypot(...b);
  a = a.map((v) => v / la); b = b.map((v) => v / lb);
  const v = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const s = Math.hypot(...v), c = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  if (s < 1e-12) return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  const vx = [[0, -v[2], v[1]], [v[2], 0, -v[0]], [-v[1], v[0], 0]];
  const vx2 = mal(vx, vx), k = (1 - c) / (s * s);
  return [0, 1, 2].map((i) => [0, 1, 2].map((j) => (i === j ? 1 : 0) + vx[i][j] + vx2[i][j] * k));
}


/* ── 1. Flasche finden ─────────────────────────────────────── */

/* Lage der Flasche (in Pixeln des Arbeitsbilds) aus dem verkleinerten
   Foto: an mehreren Stellen gesucht, die beste Lage gilt. */
function flascheFinden(bild, vorlage) {
  const ueb = verkleinern(bild, 960);
  const q = bild.height / ueb.height;
  const lum = luminanz(ueb);
  const W = ueb.width, H = ueb.height;
  let best = null;
  suche: for (const cy of [0.5, 0.44, 0.56]) {
    for (const cx of [0.5, 0.42, 0.58, 0.34, 0.66]) {
      const S = 0.72 * H / vorlage.uBoden;
      const r = lageSuchen(lum, { cx: cx * W, oben: cy * H - 0.5 * vorlage.uBoden * S, S }, vorlage);
      if (r && (!best || r.guete > best.guete)) best = r;
      if (best && best.guete >= 0.8) break suche;
    }
  }
  if (!best || best.guete < 0.4) return null;
  const l = best.lage;
  return { lage: { cx: l.cx * q, neig: l.neig, R: l.R * q, oben: l.oben * q, S: l.S * q }, guete: best.guete };
}


/* Ohne eingeblendeten Umriss weiß niemand, wo und wie groß die
   Flasche im Foto steht. Gesucht wird darum, was jede Flasche hat:
   einen Bauch mit zwei langen, fast senkrechten Kanten, links und
   rechts der Achse. Erst die Geraden (Hough: jede Kante stimmt für
   alle Geraden durch sie, über eine Spanne von Neigungen — das Telefon
   ist selten genau senkrecht gehalten, und von oben fotografiert
   laufen die Kanten nach unten zusammen), dann die Paare: Es gewinnt
   das Paar, das über die meisten Zeilen beidseitig belegt ist und die
   Gestalt einer Flasche hat. Etikettränder bilden auch Paare, aber nur
   über die Etiketthöhe; ein Türrahmen ist viel zu lang für seine
   Breite; Rauputz bildet keine Geraden. Die Oberkante ist dann die
   oberste deutliche Kante entlang der Achse. Geliefert wird die Lage
   wie bei der Erkennung im Video, dazu die Bauchzeilen. */
function flascheSymmetrisch(bild) {
  const ueb = verkleinern(bild, 960);
  const q = bild.height / ueb.height;
  const lum = luminanz(ueb);
  const W = ueb.width, H = ueb.height, yc = H / 2;
  /* nur steile Kanten: Auf gestreiftem Stoff war sonst jede Zeile voller
     Kanten, und jedes Geradenpaar galt als durchgehend belegt */
  const { ks: k } = kantenbild(ueb, lum, 0, W - 1, true);
  const probe = [];
  for (let i = 0; i < k.length; i += 7) probe.push(k[i]);
  probe.sort((a, b) => a - b);
  /* Schwelle: ein Viertel der stärksten Kanten, mindestens 12 — aber
     wo die Umgebung glatt ist, weniger (bis 7): Eine dunkle Flasche vor
     ihrem eigenen Schatten an der Wand hat kaum mehr Kontrast, und
     dort stört kein Rauputz. Maß der Umgebung: das örtliche Mittel
     links und rechts daneben (ohne die Kante selbst), das kleinere —
     eine Seite einer Flaschenkante ist immer glattes Glas. */
  const T = Math.max(12, 0.25 * probe[Math.floor(probe.length * 0.99)]);
  const umgebung = gauss(k, W, H, 6), D = 12;
  const glatt = (o, x) => Math.min(umgebung[o - Math.min(D, x)], umgebung[o + Math.min(D, W - 1 - x)]);
  /* Kantenpunkte: örtliche Maxima je Zeile; E: dieselben, ±2 Pixel breit */
  const E = new Uint8Array(W * H), px = [], py = [];
  for (let y = 0; y < H; y++) {
    for (let x = 1; x < W - 1; x++) {
      const o = y * W + x, v = k[o];
      if (v < T && (v < 7 || v < 2.5 * glatt(o, x))) continue;
      for (let d = -2; d <= 2; d++) E[o + d] = 1;
      if (v >= k[o - 1] && v > k[o + 1]) { px.push(x); py.push(y); }
    }
  }
  /* Geraden x = x0 + m·(y − yc) */
  const M = [];
  for (let m = -0.12; m <= 0.1201; m += 0.004) M.push(m);
  const NM = M.length;
  const stimmen = new Uint16Array(NM * W);
  for (let i = 0; i < px.length; i++) {
    const dy = py[i] - yc;
    for (let j = 0; j < NM; j++) {
      const x0 = Math.round(px[i] - M[j] * dy);
      if (x0 >= 0 && x0 < W) stimmen[j * W + x0]++;
    }
  }
  const wert = new Float32Array(NM * W);
  for (let j = 0; j < NM; j++) for (let x = 1; x < W - 1; x++) wert[j * W + x] = stimmen[j * W + x - 1] + stimmen[j * W + x] + stimmen[j * W + x + 1];
  /* die stärksten Geraden, jede mit ihrem längsten belegten Lauf */
  const luecke = Math.round(0.04 * H);
  const belegtBei = (xBei) => (y) => { const x = xBei(y); return x >= 0 && x < W && E[y * W + x] === 1; };
  const lauf = (belegtIn, y0 = 2, y1 = H - 3) => {
    let best = null, start = -1, zuletzt = -1e9, belegt = 0, bBelegt = 0;
    for (let y = y0; y <= y1; y++) {
      if (!belegtIn(y)) continue;
      if (y - zuletzt > luecke) { start = y; belegt = 0; }
      zuletzt = y; belegt++;
      if (!best || belegt > bBelegt) { best = [start, zuletzt]; bBelegt = belegt; }
    }
    return best && { von: best[0], bis: best[1], belegt: bBelegt };
  };
  /* Enden eines Laufs, die nur noch vereinzelt belegt sind, kappen
     (weniger als die Hälfte der Zeilen im Fenster): Über der Schulter
     überbrücken sonst Putz- oder Stoffkanten die Lücken, und der Bauch
     reichte bis an den Hals. */
  const dicht = (belegtIn, l) => {
    if (!l) return l;
    const anteil = (a, e) => { let z = 0; for (let y = a; y <= e; y++) if (belegtIn(y)) z++; return z / (e - a + 1); };
    let { von, bis } = l;
    while (bis - von > 2 * luecke && anteil(von, von + luecke) < 0.5) von++;
    while (bis - von > 2 * luecke && anteil(bis - luecke, bis) < 0.5) bis--;
    let belegt = 0;
    for (let y = von; y <= bis; y++) if (belegtIn(y)) belegt++;
    return { von, bis, belegt };
  };
  const geraden = [];
  const minStimmen = 0.08 * H;
  for (let versuch = 0; versuch < 60 && geraden.length < 40; versuch++) {
    let best = -1, bw = 0;
    for (let i = 0; i < wert.length; i++) if (wert[i] > bw) { bw = wert[i]; best = i; }
    if (best < 0 || bw < minStimmen) break;
    const j = Math.floor(best / W), x0 = best % W, m = M[j];
    for (let jj = Math.max(0, j - 6); jj <= Math.min(NM - 1, j + 6); jj++) {
      for (let x = Math.max(0, x0 - 4); x <= Math.min(W - 1, x0 + 4); x++) wert[jj * W + x] = 0;
    }
    const xBei = (y) => Math.round(x0 + m * (y - yc));
    const l = lauf(belegtBei(xBei));
    if (l && l.belegt >= minStimmen) geraden.push({ x0, m, xBei, ...l });
  }
  /* Paare */
  const wMin = Math.max(6, 0.025 * H), wMax = 0.22 * H;
  let wahl = null;
  for (const L of geraden) {
    for (const R of geraden) {
      if (R.x0 <= L.x0) continue;
      const von = Math.max(L.von, R.von), bis = Math.min(L.bis, R.bis);
      if (bis - von < 1.5 * wMin) continue;
      const ym = (von + bis) / 2, w = (R.xBei(ym) - L.xBei(ym)) / 2;
      if (w < wMin || w > wMax || bis - von < 1.5 * w) continue;
      /* von oben fotografiert wird der Bauch nach unten schmaler (aus
         der Nähe und steil von oben um ein Siebtel), aber nicht viel */
      if (Math.abs(R.m - L.m) > 0.08 || Math.abs(R.m - L.m) * (bis - von) / 2 > 0.3 * w) continue;
      const bl = belegtBei(L.xBei), br = belegtBei(R.xBei);
      const beide = (y) => bl(y) && br(y);
      const b = dicht(beide, lauf(beide, von, bis));
      if (!b || (wahl && b.belegt * Math.pow(w, 0.25) <= wahl.wert)) continue;
      const r = flascheAn(L, R, b);
      if (!r) continue;
      /* Der Hals sitzt mittig: Ein Paar aus Glanzstreifen und Umriss
         ist ebenso lang, aber seine Achse geht am Hals vorbei. Und der
         Umriss ist außen: Bei fast gleicher Stütze gewinnt das breitere
         Paar (Etikett- und Glanzkanten liegen innen). */
      const wert = b.belegt * Math.max(0, 1 - 2.5 * Math.max(0, r.halsVersatz - 0.04)) * Math.pow(w, 0.25);
      if (!wahl || wert > wahl.wert || (wert === wahl.wert && w > wahl.w)) wahl = { wert, w, L, R, ...r };
    }
  }
  if (!wahl) return null;
  const { L, R, oben, w } = wahl;
  const neig = (L.m + R.m) / 2, yBez = oben + 4 * w;
  const achse = (y) => (L.x0 + L.m * (y - yc) + R.x0 + R.m * (y - yc)) / 2;
  return {
    lage: { cx: achse(yBez) * q, neig, R: w * q, oben: oben * q, S: w * q },
    bauch: [wahl.von * q, wahl.bis * q], guete: 1,
  };

  /* Gestalt einer Flasche: Bauch (beidseitig belegt) 1,5 bis 8 Radien
     lang (mit Spiegelbild auf der Abstellfläche); darüber entlang der
     Achse die oberste deutliche Stufe in Helligkeit oder Buntheit, die
     Oberkante, höchstens 8 Radien über dem Bauch. */
  function flascheAn(L, R, b) {
    const ym = (b.von + b.bis) / 2, w = (R.xBei(ym) - L.xBei(ym)) / 2;
    if (b.bis - b.von < 1.5 * w || b.bis - b.von > 8 * w) return null;
    const achse = (y) => Math.round((L.x0 + R.x0) / 2 + (L.m + R.m) / 2 * (y - yc));
    const rn = Math.max(2, Math.round(0.2 * w));
    const bis = Math.max(4, b.von - Math.round(1.5 * w));
    const hell = new Float32Array(bis + 3), farbe = new Float32Array(bis + 3);
    for (let y = 0; y < bis + 3 && y < H; y++) {
      const a = achse(y);
      let s = 0, f = 0, n = 0;
      for (let x = Math.max(0, a - rn); x <= Math.min(W - 1, a + rn); x++) {
        const o = y * W + x, p = o * 4;
        s += lum.g[o]; f += Math.max(ueb.data[p], ueb.data[p + 1], ueb.data[p + 2]) - Math.min(ueb.data[p], ueb.data[p + 1], ueb.data[p + 2]); n++;
      }
      hell[y] = n ? s / n : 0; farbe[y] = n ? f / n : 0;
    }
    const sprung = new Float32Array(bis + 1);
    let max = 0;
    for (let y = 2; y < bis; y++) {
      sprung[y] = Math.max(Math.abs(hell[y + 2] + hell[y + 1] - hell[y - 1] - hell[y - 2]), 0.8 * Math.abs(farbe[y + 2] + farbe[y + 1] - farbe[y - 1] - farbe[y - 2])) / 2;
      max = Math.max(max, sprung[y]);
    }
    let oben = -1;
    for (let y = 3; y < bis - 1; y++) {
      if (sprung[y] >= Math.max(10, 0.35 * max) && sprung[y] >= sprung[y - 1] && sprung[y] >= sprung[y + 1]) { oben = y; break; }
    }
    if (oben < 0 || b.von - oben > 8 * w) return null;
    /* Hals: im oberen Teil zwischen Oberkante und Bauch je Zeile die
       stärkste Kante links und rechts der Achse; ihre Mitte gegen die
       Achse, in Bauchradien */
    const mitten = [];
    for (let y = Math.round(oben + 0.1 * (b.von - oben)); y <= Math.round(oben + 0.4 * (b.von - oben)); y++) {
      const a = achse(y), seite = (s0, s1) => {
        let bx = -1, bv = T;
        for (let x = Math.max(1, Math.round(s0)); x <= Math.min(W - 2, Math.round(s1)); x++) if (k[y * W + x] > bv) { bv = k[y * W + x]; bx = x; }
        return bx;
      };
      const l = seite(a - 0.8 * w, a - 0.15 * w), r = seite(a + 0.15 * w, a + 0.8 * w);
      if (l >= 0 && r >= 0) mitten.push((l + r) / 2 - a);
    }
    const halsVersatz = mitten.length >= 5 ? Math.abs(median(mitten)) / w : 0.2;
    return { oben, von: b.von, bis: b.bis, w, halsVersatz };
  }
}


/* ── 2. Umriss in voller Auflösung ─────────────────────────── */

/* Kantenbild: waagerechter Gradient von Helligkeit und Buntheit, beide
   leicht geglättet — der Rauputz einer Wand hat in voller Auflösung
   kräftige, aber kleine Kanten, der Umriss ist lang und durchgehend. */
function kantenbild(bild, lum, xa = 0, xe = bild.width - 1, steil = false) {
  const { width: W, height: H, data } = bild;
  /* nur im Streifen um die Flasche: außerhalb bleibt das Kantenbild 0 */
  xa = Math.max(0, Math.floor(xa)); xe = Math.min(W - 1, Math.ceil(xe));
  const wc = xe - xa + 1;
  const g0 = new Float32Array(wc * H), b0 = new Float32Array(wc * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < wc; x++) {
      const i = y * W + xa + x, o = i * 4, z = y * wc + x;
      g0[z] = lum.g[i];
      b0[z] = Math.max(data[o], data[o + 1], data[o + 2]) - Math.min(data[o], data[o + 1], data[o + 2]);
    }
  }
  const g = gauss(g0, wc, H, 1.2), b = gauss(b0, wc, H, 1.2);
  const k = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    const o = y * W + xa, q = y * wc;
    for (let x = 1; x < wc - 1; x++) k[o + x] = Math.max(Math.abs(g[q + x + 1] - g[q + x - 1]), 0.8 * Math.abs(b[q + x + 1] - b[q + x - 1]));
  }
  if (!steil) return { k, W, H };
  /* dazu nur die steilen Kanten (ks): Bauch und Hals einer stehenden
     Flasche laufen fast senkrecht, ihr Gradient fast waagerecht. Ein
     gestreifter oder gemusterter Untergrund (Kissen, Tischdecke) hat
     kräftige Kanten in allen Richtungen — ist der senkrechte Gradient
     mehr als halb so groß wie der waagerechte (Kante mehr als 27° gegen
     die Senkrechte geneigt), zählt sie dort nicht. */
  const ks = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++) {
    const o = y * W + xa, q = y * wc;
    for (let x = 1; x < wc - 1; x++) {
      const s = Math.max(Math.abs(g[q + x + wc] - g[q + x - wc]), 0.8 * Math.abs(b[q + x + wc] - b[q + x - wc]));
      if (s <= 0.5 * k[o + x]) ks[o + x] = k[o + x];
    }
  }
  return { k, ks, W, H };
}

/* Alle deutlichen Kanten einer Zeile (örtliche Maxima ab 20 % der
   stärksten): Neben einem kräftigen Etikettrand darf der schwache
   Umriss an der hell beschienenen Seite nicht verloren gehen */
function kantenKandidaten(kb, y, x0, x1) {
  const { k, W } = kb;
  const a = Math.max(1, Math.round(Math.min(x0, x1))), e = Math.min(W - 2, Math.round(Math.max(x0, x1)));
  let max = 0;
  for (let x = a; x <= e; x++) max = Math.max(max, k[y * W + x]);
  const aus = [];
  if (!(max > 0)) return aus;
  for (let x = a; x <= e; x++) {
    const v = k[y * W + x];
    if (v < Math.max(6, 0.2 * max) || v < k[y * W + x - 1] || v < k[y * W + x + 1]) continue;
    const l = k[y * W + x - 1], r = k[y * W + x + 1], d = l - 2 * v + r;
    aus.push({ x: x + (d < 0 ? Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d)) : 0), v });
  }
  return aus;
}

/* Gerade Kanten aus den Kandidaten aller Zeilen (RANSAC): je Gerade
   die Zahl der Zeilen, die nahe an ihr eine Kante haben. Geliefert
   werden die gut gestützten, ohne Doppelte, nach Stützung geordnet. */
function geradenKandidaten(ys, xs, aussen, mindestZeilen) {
  const n = ys.length;
  if (n < 10) return [];
  let z = 98765;
  const rnd = () => { z = (z * 1664525 + 1013904223) >>> 0; return z / 4294967296; };
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const stuetze = (a, b) => {
    const zeilen = new Set();
    let summe = 0;
    for (let i = 0; i < n; i++) if (Math.abs(xs[i] - (a + b * ys[i])) < 1.5 && !zeilen.has(ys[i])) { zeilen.add(ys[i]); summe += aussen[i]; }
    return { anzahl: zeilen.size, aussen: zeilen.size ? summe / zeilen.size : 0 };
  };
  const alle = [];
  for (let it = 0; it < 400; it++) {
    const i = Math.floor(rnd() * n), j = Math.floor(rnd() * n);
    if (Math.abs(ys[i] - ys[j]) < 0.3 * (yMax - yMin)) continue;
    const b = (xs[j] - xs[i]) / (ys[j] - ys[i]);
    if (Math.abs(b) > 0.15) continue;
    const a = xs[i] - b * ys[i];
    alle.push({ a, b, ...stuetze(a, b) });
  }
  alle.sort((p, q) => q.anzahl - p.anzahl);
  const aus = [];
  for (const k of alle) {
    if (k.anzahl < Math.max(5, 0.15 * mindestZeilen) || (aus.length && k.anzahl < 0.25 * aus[0].anzahl)) break;
    const gleich = aus.some((g) => Math.abs(g.a + g.b * yMin - k.a - k.b * yMin) < 2.5 && Math.abs(g.a + g.b * yMax - k.a - k.b * yMax) < 2.5);
    if (!gleich) aus.push(k);
    if (aus.length >= 6) break;
  }
  /* nachrechnen mit den Punkten nahe der Geraden (je Zeile der nächste) */
  return aus.map((k) => {
    const jeZeile = new Map();
    for (let i = 0; i < n; i++) {
      const d = Math.abs(xs[i] - (k.a + k.b * ys[i]));
      if (d < 1.5 && (!jeZeile.has(ys[i]) || d < jeZeile.get(ys[i])[1])) jeZeile.set(ys[i], [xs[i], d]);
    }
    const yy = [...jeZeile.keys()], xx = yy.map((y) => jeZeile.get(y)[0]);
    const g = robusteGerade(yy, xx, 3, 0.3);
    return g ? { ...k, a: g.a, b: g.b, streuung: g.streuung, anteil: yy.length / (mindestZeilen || 1) } : { ...k, anteil: k.anzahl / (mindestZeilen || 1) };
  });
}

/* Kandidaten einer Zone: je Zeile alle deutlichen Kanten im Fenster
   [innen, außen] (Vielfache von R um die Achse) */
function zonenKandidaten(kb, achse, seite, y0, y1, innen, aussen) {
  const ys = [], xs = [], vs = [];
  for (let y = y0; y <= y1; y += 2) {
    const a = achse(y);
    for (const e of kantenKandidaten(kb, y, a + seite * innen, a + seite * aussen)) { ys.push(y); xs.push(e.x); vs.push(seite * (e.x - a)); }
  }
  return geradenKandidaten(ys, xs, vs, (y1 - y0) / 2);
}

/* Umriss je Seite als bester durchgehender Pfad von der Mündung bis
   unter den Boden (dynamische Programmierung): je Zeile eine Stelle in
   einem Band um den Umriss der Grundform, von Zeile zu Zeile höchstens
   zwei Pixel versetzt (jeder kostet), Summe der Kantenstärken möglichst
   groß. Ein Pfad muss durchgehen — der Schatten der Flasche an der
   Wand, der an der Schulter vom Glas abzweigt, und der Rauputz bieten
   das nicht. Im Bauch liegt das Band eng an den Geraden.
   Ein leichter Zug nach außen bevorzugt den Umriss vor Glanzstreifen
   knapp innerhalb. */
const KANTE_MAX = 40;
function umrissPfad(kb, achse, seite, prior, y0, y1, band, R, bandBei = () => band, steilBei = () => false) {
  const { W } = kb;
  const n = y1 - y0 + 1, nb = 2 * band + 1;
  let acc = new Float32Array(nb), neu = new Float32Array(nb);
  const zurueck = new Int8Array(n * nb);
  const basis = (y) => Math.round(prior(y)) - band;
  /* wo der Umriss steil laufen soll, nur steile Kanten (Muster im
     Untergrund); an der Schulter alle */
  const kBei = (y) => (kb.ks && steilBei(y) ? kb.ks : kb.k);
  const wert = (y, j, k) => {
    const x = basis(y) + j;
    if (x < 1 || x > W - 2) return -20;
    if (Math.abs(j - band) > bandBei(y)) return -1e6;
    const d = seite * (x - achse(y));
    if (d < 0.08 * R) return -50;
    return Math.min(KANTE_MAX, k[y * W + x]) + 0.02 * KANTE_MAX * (j - band) * seite / band;
  };
  for (let j = 0; j < nb; j++) acc[j] = wert(y0, j, kBei(y0));
  for (let i = 1; i < n; i++) {
    const y = y0 + i, verschub = basis(y) - basis(y - 1), k = kBei(y);
    for (let j = 0; j < nb; j++) {
      let best = -Infinity, bd = 0;
      for (let d = -2; d <= 2; d++) {
        const jv = j + verschub - d;
        if (jv < 0 || jv >= nb) continue;
        const v = acc[jv] - 3 * Math.abs(d);
        if (v > best) { best = v; bd = d; }
      }
      neu[j] = (best === -Infinity ? -1e9 : best) + wert(y, j, k);
      zurueck[i * nb + j] = bd;
    }
    [acc, neu] = [neu, acc];
  }
  let j = 0;
  for (let t = 1; t < nb; t++) if (acc[t] > acc[j]) j = t;
  const xs = new Float32Array(n), staerke = new Float32Array(n);
  for (let i = n - 1; i >= 0; i--) {
    const y = y0 + i, x = basis(y) + j, k = kBei(y);
    /* auf Bruchteile: Parabel durch die Nachbarn */
    const o = y * W + x, l = k[o - 1], m = k[o], r = k[o + 1], dd = l - 2 * m + r;
    xs[i] = x + (dd < 0 && m >= l && m >= r ? Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / dd)) : 0);
    staerke[i] = m;
    if (i > 0) { const d = zurueck[i * nb + j]; j = j + (basis(y) - basis(y - 1)) - d; j = Math.max(0, Math.min(nb - 1, j)); }
  }
  /* zu schwache Stellen verwerfen */
  const med = median(Array.from(staerke));
  for (let i = 0; i < n; i++) if (staerke[i] < 0.25 * med) xs[i] = NaN;
  return xs;
}

function umrissMessen(bild, lum, lage, vorlage, bauchZeilen = null) {
  const H = bild.height;
  const yBez = lage.oben + 4 * lage.S;
  const achse0 = (y) => lage.cx + lage.neig * (y - yBez);
  const y0 = Math.max(2, Math.round(lage.oben + 0.02 * lage.S));
  const y1 = Math.min(H - 3, Math.round(Math.max(lage.oben + 1.2 * lage.S * vorlage.uBoden, bauchZeilen ? bauchZeilen[1] + 2 * lage.R : 0)));
  if (!(y1 > y0 + 50)) return null;
  const rand = 1.8 * lage.R + 12;
  const kb = kantenbild(bild, lum, Math.min(achse0(y0), achse0(y1)) - rand, Math.max(achse0(y0), achse0(y1)) + rand, true);
  /* Bauch- und Halsgeraden aus den steilen Kanten (gemusterter Untergrund) */
  const kbSteil = { k: kb.ks, W: kb.W, H: kb.H };
  /* Bauch zuerst, als Geraden. Welche Kante der Umriss ist, entscheidet
     der Hals: Die Flasche ist ein Drehkörper, ihre Achse geht durch die
     Mitte des Halses ebenso wie durch die Mitte des Bauchs. Ein Etikett,
     das bis an den Rand reicht und so hell ist wie die Wand, zeigt dort
     nur seinen eigenen Rand, knapp innen und über die ganze
     Etiketthöhe — das Paar mit ihm steht schief zur Halsmitte. */
  const [u0, u1] = vorlage.bauch;
  /* Bauchzeilen: gemessen (Symmetriesuche) oder nach der Grundform */
  const bz = bauchZeilen ? [bauchZeilen[0] + 0.06 * (bauchZeilen[1] - bauchZeilen[0]), bauchZeilen[1] - 0.06 * (bauchZeilen[1] - bauchZeilen[0])]
    : [lage.oben + (u0 + 0.3) * lage.S, lage.oben + (u1 - 0.2) * lage.S];
  const yb0 = Math.max(y0, Math.round(bz[0])), yb1 = Math.min(y1, Math.round(bz[1]));
  const [kl, kr] = [-1, 1].map((seite) => zonenKandidaten(kbSteil, achse0, seite, yb0, yb1, 0.8 * lage.R, 1.25 * lage.R));
  if (!kl.length || !kr.length) return null;
  const [h0, h1] = vorlage.hals;
  const yh0 = Math.max(y0, Math.round(lage.oben + (h0 + 0.1) * lage.S)), yh1 = Math.round(lage.oben + (h1 - 0.1) * lage.S);
  const rH = vorlage.rHals * lage.R;
  const [hl, hr] = yh1 > yh0 + 20 ? [-1, 1].map((seite) => zonenKandidaten(kbSteil, achse0, seite, yh0, yh1, 0.5 * rH, 1.7 * rH)) : [[], []];
  const yh = (yh0 + yh1) / 2;
  let halsMitte = achse0(yh), halsGewicht = 4;
  if (hl.length && hr.length) {
    let bestH = null;
    for (const l of hl) for (const r of hr) {
      const w = ((r.a + r.b * yh) - (l.a + l.b * yh)) / 2;
      if (w < 0.6 * rH || w > 1.5 * rH) continue;
      const wert = l.anzahl + r.anzahl;
      if (!bestH || wert > bestH.wert) bestH = { wert, mitte: ((l.a + l.b * yh) + (r.a + r.b * yh)) / 2 };
    }
    if (bestH) { halsMitte = bestH.mitte; halsGewicht = 8; }
  }
  const ml = kl[0].anzahl, mr = kr[0].anzahl;
  let best = null;
  for (const l of kl) for (const r of kr) {
    const mitte = ((l.a + l.b * yh) + (r.a + r.b * yh)) / 2;
    /* die Breite aus der Erkennung zählt mit: Ein mittiges Etikett hat
       zwei Ränder, symmetrisch zum Hals wie der Umriss, aber schmaler */
    const breite = ((r.a + r.b * yh) - (l.a + l.b * yh)) / 2;
    const wert = l.anzahl / ml + r.anzahl / mr - halsGewicht * Math.abs(mitte - halsMitte) / lage.R
      - 3 * Math.abs(breite - lage.R) / lage.R + 0.25 * (l.aussen + r.aussen) / (2 * lage.R);
    if (!best || wert > best.wert) best = { wert, l, r };
  }
  const geraden = [best.l, best.r];
  const xL = (y) => geraden[0].a + geraden[0].b * y, xR = (y) => geraden[1].a + geraden[1].b * y;
  const achse = (y) => (xL(y) + xR(y)) / 2;
  /* Grundform als Vorgabe; unter dem Bauch weiter wie der Bauch (die
     Flasche ist oft höher als die Grundform, darunter folgt ihr
     Spiegelbild auf der Abstellfläche) */
  const sv = bauchZeilen ? Math.max(0.7 * lage.S, Math.min(1.4 * lage.S, (bauchZeilen[0] - lage.oben) / Math.max(0.5, u0))) : lage.S;
  const rhoT = (u) => {
    u = Math.min(u * lage.S / sv, u1);
    const p = u / vorlage.du, i = Math.max(0, Math.min(vorlage.rho.length - 2, Math.floor(p)));
    return vorlage.rho[i] + (vorlage.rho[i + 1] - vorlage.rho[i]) * Math.max(0, Math.min(1, p - i));
  };
  /* Dann je Seite der Pfad: im Bauch eng an der Geraden, darüber und
     darunter weit um die Grundform (Hals und Schulter sitzen bei jeder
     Flasche etwas anders) */
  const R = (xR((yb0 + yb1) / 2) - xL((yb0 + yb1) / 2)) / 2;
  const band = Math.max(8, Math.round(0.3 * R)), eng = Math.max(3, Math.round(0.04 * R));
  const imBauch = (y) => y >= yb0 && y <= yb1;
  const [links, rechts] = [-1, 1].map((seite) => {
    const prior = (y) => (imBauch(y) ? (seite < 0 ? xL(y) : xR(y)) : achse(y) + seite * rhoT((y - lage.oben) / lage.S) * R);
    /* steil (unter 14°): Bauch, Hals und unter dem Bauch; an der Schulter nicht */
    const steil = (y) => Math.abs(prior(y + 3) - prior(y - 3)) < 1.5;
    return umrissPfad(kb, achse, seite, prior, y0, y1, band, R, (y) => (imBauch(y) ? eng : band), steil);
  });
  return { y0, n: y1 - y0 + 1, links, rechts, achse, geraden };
}

/* ── 3./4. Entzerren und Gestalt ───────────────────────────── */

/* Gleitender Median über Fenster (NaN bleiben draußen) */
function gleitMedian(v, fenster) {
  const h = Math.floor(fenster / 2), aus = new Float32Array(v.length).fill(NaN);
  for (let i = 0; i < v.length; i++) {
    const w = [];
    for (let k = Math.max(0, i - h); k <= Math.min(v.length - 1, i + h); k++) if (Number.isFinite(v[k])) w.push(v[k]);
    if (w.length >= 3) { w.sort((a, b) => a - b); aus[i] = w[Math.floor(w.length / 2)]; }
  }
  return aus;
}

/* Bauchzone: der längste Lauf von Zeilen, in denen die Flasche fast
   ihre volle Breite hat */
function bauchZone(breiten) {
  const werte = Array.from(breiten).filter(Number.isFinite).sort((a, b) => a - b);
  if (werte.length < 20) return null;
  const wMax = werte[Math.floor(werte.length * 0.9)];
  const voll = Array.from(breiten, (w) => Number.isFinite(w) && w > 0.9 * wMax);
  const luecke = Math.max(3, Math.round(breiten.length * 0.03));
  let best = null, start = -1, zuletzt = -1;
  for (let i = 0; i <= voll.length; i++) {
    if (i < voll.length && voll[i]) {
      if (start < 0 || i - zuletzt > luecke) start = i;
      zuletzt = i;
      if (!best || zuletzt - start > best[1] - best[0]) best = [start, zuletzt];
    }
  }
  if (!best || best[1] - best[0] < breiten.length * 0.15) return null;
  const rand = Math.round(0.04 * (best[1] - best[0]));
  return [best[0] + rand, best[1] - rand, wMax];
}

/* Rektifizierung bei Brennweite f: senkrechte Fluchtrichtung → Bild-
   senkrechte, dann gieren, bis die Achse in der Bildmitte steht. */
function rektifizierung(f, cx, cy, flucht, achsPunkt, kipp = 0) {
  const Ki = kInv(f, cx, cy);
  let d = [Ki[0][0] * flucht[0] + Ki[0][2] * flucht[2], Ki[1][1] * flucht[1] + Ki[1][2] * flucht[2], flucht[2]];
  if (d[1] < 0) d = d.map((v) => -v);
  const R1 = drehungAuf(d, [0, 1, 0]);
  const p0 = [(achsPunkt[0] - cx) / f, (achsPunkt[1] - cy) / f, 1];
  const p = [0, 1, 2].map((i) => R1[i][0] * p0[0] + R1[i][1] * p0[1] + R1[i][2] * p0[2]);
  const a = Math.atan2(p[0], p[2]);
  const R2 = [[Math.cos(a), 0, -Math.sin(a)], [0, 1, 0], [Math.sin(a), 0, Math.cos(a)]];
  const RK = [[1, 0, 0], [0, Math.cos(kipp), -Math.sin(kipp)], [0, Math.sin(kipp), Math.cos(kipp)]];
  return mal(RK, mal(R2, R1));
}

function homographien(f, cx, cy, R) {
  const K = kMat(f, cx, cy), Ki = kInv(f, cx, cy);
  const Hm = mal(K, mal(R, Ki));
  const Hinv = mal(K, mal(transp(R), Ki));
  return { Hm, Hinv };
}

/* Je Foto: Fluchtrichtung, Achspunkt, Bauchbreite und die Gestalt
   (s, ρ) bei der Brennweite f0. */
function gestaltMessen(bild, lum, lage, vorlage, bauchZeilen = null) {
  const W = bild.width, H = bild.height, cx = W / 2, cy = H / 2;
  const um = umrissMessen(bild, lum, lage, vorlage, bauchZeilen);
  if (!um) return null;
  const { y0, n, links, rechts } = um;
  const breiten = new Float32Array(n);
  for (let i = 0; i < n; i++) breiten[i] = (rechts[i] - links[i]) / 2;
  let zone = bauchZone(gleitMedian(breiten, 9));
  if (!zone) return null;
  /* nicht über den erkannten Bauch hinaus: Unter dem Boden läuft der
     Umriss auf gemustertem Untergrund in Bauchbreite weiter, und schon
     wenige falsche Zeilen kippen die Geraden (Fluchtpunkt) */
  if (bauchZeilen) {
    const a = Math.max(zone[0], Math.round(bauchZeilen[0] - y0)), e = Math.min(zone[1], Math.round(bauchZeilen[1] - y0));
    if (e - a > 0.15 * n) zone = [a, e, zone[2]];
  }
  const ys = [], xl = [], xr = [];
  for (let i = zone[0]; i <= zone[1]; i++) {
    if (Number.isFinite(links[i]) && Number.isFinite(rechts[i])) { ys.push(y0 + i); xl.push(links[i]); xr.push(rechts[i]); }
  }
  const gl = robusteGerade(ys, xl, 5, 0.3), gr = robusteGerade(ys, xr, 5, 0.3);
  if (!gl || !gr || gl.anteil < 0.35 || gr.anteil < 0.35) return null;
  /* Geraden x = a + b·y homogen: (1, −b, −a); Fluchtpunkt = Kreuzprodukt */
  const l1 = [1, -gl.b, -gl.a], l2 = [1, -gr.b, -gr.a];
  const flucht = [l1[1] * l2[2] - l1[2] * l2[1], l1[2] * l2[0] - l1[0] * l2[2], l1[0] * l2[1] - l1[1] * l2[0]];
  const ym = (ys[0] + ys[ys.length - 1]) / 2;
  const achsPunkt = [(gl.a + gl.b * ym + gr.a + gr.b * ym) / 2, ym];
  const wBauch = ((gr.a - gl.a) + (gr.b - gl.b) * ym) / 2;
  /* Oberkante fein, entlang der gemessenen Achse */
  const lageFein = { ...lage, cx: achsPunkt[0], neig: (gl.b + gr.b) / 2 };
  lageFein.cx = achsPunkt[0] + lageFein.neig * (lage.oben + 4 * lage.S - ym);
  const oben = oberkanteSuchen(lum, lageFein, vorlage, 0.035 * lage.S * vorlage.uBoden) ?? lage.oben;
  const yBez = lage.oben + 4 * lage.S;
  const obenPunkt = [lageFein.cx + lageFein.neig * (oben - yBez), oben];
  return { W, H, cx, cy, um, flucht, achsPunkt, wBauch, obenPunkt, zone: [y0 + zone[0], y0 + zone[1]] };
}

/* Gestalt im entzerrten Bild bei Brennweite f: je Zeile k = r/D und
   die Höhe; daraus (s, ρ) in Bauchradien ab der Oberkante. */
function gestaltRechnen(m, f) {
  const { cx, cy, um } = m;
  const R = rektifizierung(f, cx, cy, m.flucht, m.achsPunkt, m.kipp || 0);
  const { Hm, Hinv } = homographien(f, cx, cy, R);
  /* Kantenpunkte entzerren; je Seite nach Zeilen ordnen */
  const seiten = [um.links, um.rechts].map((xs, si) => {
    const pts = [];
    for (let i = 0; i < um.n; i++) {
      if (!Number.isFinite(xs[i])) continue;
      const [x, y] = anwenden(Hm, xs[i], um.y0 + i);
      pts.push([y, si === 0 ? cx - x : x - cx]);
    }
    return pts.sort((a, b) => a[0] - b[0]);
  });
  const yMin = Math.ceil(Math.min(...seiten.map((p) => (p.length ? p[0][0] : Infinity))));
  const yMax = Math.floor(Math.max(...seiten.map((p) => (p.length ? p[p.length - 1][0] : -Infinity))));
  if (!(yMax > yMin + 20)) return null;
  const nz = yMax - yMin + 1;
  const proSeite = seiten.map((pts) => {
    const w = new Float32Array(nz).fill(NaN);
    let j = 0;
    for (let i = 0; i < nz; i++) {
      const y = yMin + i;
      while (j < pts.length - 2 && pts[j + 1][0] < y) j++;
      if (j + 1 >= pts.length) break;
      const [ya, wa] = pts[j], [yb, wb] = pts[j + 1];
      if (y < ya || y > yb || yb - ya > 3) continue;
      w[i] = wa + (wb - wa) * (y - ya) / Math.max(1e-6, yb - ya);
    }
    /* Ausreißer gegen den gleitenden Median */
    const med = gleitMedian(w, 15);
    for (let i = 0; i < nz; i++) if (Math.abs(w[i] - med[i]) > Math.max(2, 0.015 * med[i])) w[i] = NaN;
    return w;
  });
  const w = new Float32Array(nz).fill(NaN);
  for (let i = 0; i < nz; i++) {
    const a = proSeite[0][i], b = proSeite[1][i];
    /* Passen die Seiten nicht zusammen, gilt die schmalere: Fehlgriffe
       liegen bei Fotos meist außen (Rauputz, Schatten an der Wand) */
    if (Number.isFinite(a) && Number.isFinite(b)) w[i] = Math.abs(a - b) <= Math.max(3, 0.02 * Math.max(a, b)) ? (a + b) / 2 : Math.min(a, b);
    else if (Number.isFinite(a)) w[i] = a;
    else if (Number.isFinite(b)) w[i] = b;
  }
  const wg = gleitMedian(w, 9);
  const k = Float32Array.from(wg, (v) => v / Math.sqrt(f * f + v * v));
  /* Bauch im entzerrten Bild */
  const bz = m.zone.map((y) => anwenden(Hm, m.achsPunkt[0], y)[1]);
  const kbWerte = [];
  for (let i = 0; i < nz; i++) if (yMin + i > bz[0] && yMin + i < bz[1] && Number.isFinite(k[i])) kbWerte.push(k[i]);
  const kb = median(kbWerte);
  if (!(kb > 0)) return null;
  /* Oberkante: vorderster Punkt der Mündung, Tiefe D(1 − k) */
  const [, yt] = anwenden(Hm, m.obenPunkt[0], m.obenPunkt[1]);
  const kTop = median(Array.from(k.subarray(Math.max(0, Math.round(yt - yMin)), Math.max(0, Math.round(yt - yMin + 0.4 * m.wBauch)))));
  const htop = (yt - cy) * (1 - (Number.isFinite(kTop) ? kTop : 0.4 * kb)) / f;
  const s = new Float32Array(nz), rho = new Float32Array(nz);
  for (let i = 0; i < nz; i++) {
    const h = (yMin + i - cy) * (1 - k[i] * k[i]) / f;
    s[i] = (h - htop) / kb; rho[i] = k[i] / kb;
  }
  return { R, Hm, Hinv, kb, htop, s, rho };
}

/* Gemeinsame Gestalt aller Fotos: Median je Höhe, geglättet; Boden
   aus der Ferse (dort wird die Flasche schmaler, darunter beginnt
   meist ihr Spiegelbild). */
/* Profil je Foto auf dem Raster DS (bis 10 Bauchradien) */
function stufenVon(gestalten) {
  const n = Math.ceil(10 / DS);
  return gestalten.map((g) => {
    const v = new Float32Array(n).fill(NaN), sum = new Float32Array(n), cnt = new Float32Array(n);
    for (let i = 0; i < g.s.length; i++) {
      const b = Math.round(g.s[i] / DS);
      if (b >= 0 && b < n && Number.isFinite(g.rho[i])) { sum[b] += g.rho[i]; cnt[b]++; }
    }
    for (let b = 0; b < n; b++) if (cnt[b]) v[b] = sum[b] / cnt[b];
    return v;
  });
}

/* Boden je Foto: wo der Umriss nach dem Bauch schmaler zu werden
   beginnt (Suche ab `ab` Bauchradien). Darunter folgt (Kamera von
   oben) die vordere Hälfte der Bodenellipse, die schmaler wird, und
   oft das Spiegelbild auf der Abstellfläche — beides gehört nicht mehr
   zur Höhe. */
function bodenJeFoto(stufen, ab) {
  const zurueck = Math.round(1.2 / DS);
  return stufen.map((v) => {
    let imBauch = false;
    for (let b = Math.round(ab / DS); b < v.length - 2; b++) {
      if (v[b] >= 0.98) imBauch = true;
      if (!(imBauch && v[b] < 0.97 && v[b + 1] < 0.97 && v[b + 2] < 0.97)) continue;
      /* Wird der Umriss danach wieder voll breit, war es nicht der
         Boden: An einem hellen Etikett vor heller Wand verliert der
         Pfad den Umriss und folgt eine Weile den Etikettkanten. */
      let wieder = -1;
      for (let c = b + 3; c < Math.min(v.length - 2, b + zurueck); c++) {
        if (v[c] >= 0.98 && v[c + 1] >= 0.98 && v[c + 2] >= 0.98) { wieder = c; break; }
      }
      if (wieder < 0) return b * DS;
      b = wieder;
    }
    return NaN;
  });
}

/* Welche Grundform passt zur gemessenen Höhe (in Bauchradien)? Die
   gewählte bleibt, solange sie nicht deutlich danebenliegt — Bordeaux
   und Burgund etwa unterscheiden sich darin kaum. Liegt sie daneben,
   entscheidet unter allen Formen die Gestalt mit (`stufen`: Profile der
   Fotos): mittlerer Abstand der Radien über die auf die Höhe normierte
   Flasche (ohne Mündung), dazu 0,3 × Höhenabstand. Eine Cidreflasche
   ist so hoch wie eine Bordeauxflasche, hat aber die lange Schulter der
   Burgunderflasche — nach der Höhe allein lag der Bauch in der Wand. */
function passendeForm(schluessel, uBoden, stufen = null) {
  if (typeof schluessel !== "string" || !FLASCHENFORMEN[schluessel] || !(uBoden > 0)) return schluessel;
  const hoehe = (k) => Math.abs(Math.log(profilAusForm(k).uBoden / uBoden));
  if (hoehe(schluessel) <= 0.08) return schluessel;
  if (!stufen || !stufen.length) {
    const best = FORM_REIHE.reduce((a, k) => (hoehe(k) < hoehe(a) ? k : a), schluessel);
    return hoehe(best) < 0.5 * hoehe(schluessel) ? best : schluessel;
  }
  const gemessen = (sv) => median(stufen.map((v) => v[Math.round(sv / DS)]).filter(Number.isFinite));
  const proben = Array.from({ length: 40 }, (_, k) => 0.08 + 0.82 * k / 39);
  const werte = proben.map((t) => gemessen(t * uBoden));
  const gestalt = (k) => {
    const v = profilAusForm(k);
    let summe = 0, z = 0;
    proben.forEach((t, k2) => {
      if (!Number.isFinite(werte[k2])) return;
      const q = t * v.uBoden / v.du, i = Math.max(0, Math.min(v.rho.length - 2, Math.floor(q)));
      summe += Math.abs(Math.min(1.05, werte[k2]) - (v.rho[i] + (v.rho[i + 1] - v.rho[i]) * Math.max(0, Math.min(1, q - i)))); z++;
    });
    return z >= 10 ? summe / z : 1;
  };
  const wert = (k) => gestalt(k) + 0.3 * hoehe(k);
  const best = FORM_REIHE.reduce((a, k) => (wert(k) < wert(a) ? k : a), schluessel);
  return wert(best) < 0.8 * wert(schluessel) ? best : schluessel;
}

function gemeinsameGestalt(gestalten, vorlage) {
  const stufen = stufenVon(gestalten), n = stufen[0].length;
  const boeden = bodenJeFoto(stufen, 0.75 * vorlage.uBoden);
  let uBoden = median(boeden);
  if (!(uBoden > 0.85 * vorlage.uBoden && uBoden < 1.15 * vorlage.uBoden)) uBoden = vorlage.uBoden;
  const rho = new Float32Array(n).fill(NaN);
  for (let b = 0; b < n; b++) rho[b] = median(stufen.map((v) => v[b]));
  /* Lücken füllen; unter dem Boden und im Bauch höchstens 1 */
  let letzter = NaN;
  for (let b = 0; b < n; b++) { if (Number.isFinite(rho[b])) letzter = rho[b]; else rho[b] = letzter; }
  let erster = NaN;
  for (let b = n - 1; b >= 0; b--) { if (Number.isFinite(rho[b])) erster = rho[b]; else rho[b] = erster; }
  const glatt = gleitMedian(rho, 7);
  const aus = new Float32Array(n);
  for (let b = 0; b < n; b++) {
    let s = 0, c = 0;
    for (let d = -3; d <= 3; d++) { const v = glatt[Math.min(n - 1, Math.max(0, b + d))]; if (Number.isFinite(v)) { s += v; c++; } }
    aus[b] = Math.min(1.03, Math.max(0.15, c ? s / c : 1));
  }
  /* Bauch bis zum Boden durchziehen — darunter liegen Ferse und
     Spiegelbild; die Rundung am Boden gibt das Modell */
  const ab = Math.round((uBoden - 0.4) / DS);
  for (let b = ab + 1; b < n; b++) aus[b] = aus[ab];
  /* Schulter: Wo sie in den Hals übergeht, läuft der Umriss fast
     waagerecht — Zeile für Zeile ist er dort nicht zu messen, und der
     Pfad weicht in die Wand aus (dann sähe man in der Abwicklung die
     Wand). Gemessen wird nur, wo die Schulter halb so breit ist wie
     zwischen Hals und Bauch; ihre Form folgt der Grundform, gestreckt
     auf den gemessenen Hals. */
  const sB = (x) => Math.round(x / DS);
  const halsWerte = Array.from(aus.subarray(sB(0.4), sB(Math.max(0.6, vorlage.hals[1] - 0.2))));
  const rHals = median(halsWerte);
  const mitte = (rHals + 1) / 2;
  let sMitte = NaN;
  for (let b = sB(0.6); b < sB(uBoden - 1); b++) if (aus[b] < mitte && aus[b + 1] >= mitte) { sMitte = (b + (mitte - aus[b]) / (aus[b + 1] - aus[b])) * DS; break; }
  const schulter = Number.isFinite(rHals) && Number.isFinite(sMitte) && rHals > 0.15 && rHals < 0.8;
  if (schulter) {
    const rT = (u) => { const q = u / vorlage.du, i = Math.max(0, Math.min(vorlage.rho.length - 2, Math.floor(q))); return vorlage.rho[i] + (vorlage.rho[i + 1] - vorlage.rho[i]) * Math.max(0, Math.min(1, q - i)); };
    const versatz = sMitte - vorlage.schulterU;
    const von = vorlage.hals[1] + versatz, bis = vorlage.bauch[0] + versatz;
    for (let b = Math.max(0, sB(von)); b <= Math.min(n - 1, sB(bis)); b++) {
      const t = (rT(b * DS - versatz) - vorlage.rHals) / (1 - vorlage.rHals);
      aus[b] = rHals + (1 - rHals) * Math.max(0, Math.min(1, t));
    }
    for (let b = sB(bis) + 1; b < n && b < sB(bis + 0.5); b++) aus[b] = Math.max(aus[b], 0.97);
    for (let b = 0; b < sB(von); b++) aus[b] = Math.min(aus[b], rHals * 1.08);
  }
  return { rho: aus, uBoden, schulter: schulter ? [vorlage.hals[1] + sMitte - vorlage.schulterU, vorlage.bauch[0] + sMitte - vorlage.schulterU] : null };
}

const rhoVon = (gestalt) => (s) => {
  const p = s / DS;
  if (!(p >= 0)) return gestalt.rho[0];
  const i = Math.floor(p);
  if (i >= gestalt.rho.length - 1) return gestalt.rho[gestalt.rho.length - 1];
  return gestalt.rho[i] + (gestalt.rho[i + 1] - gestalt.rho[i]) * (p - i);
};


/* ── 5. Abbildung zwischen Flasche und Foto ───────────────── */

/* Punkt (s, ψ) der Flasche → Pixel des Fotos; null, wenn verdeckt */
function abbilden(foto, rho, f, s, psi) {
  const r = rho(s) * foto.kb, c = Math.cos(psi);
  if (c <= r) return null;
  const Z = 1 - r * c;
  return anwenden(foto.Hinv, foto.cx + f * r * Math.sin(psi) / Z, foto.cy + f * (foto.htop + s * foto.kb) / Z);
}

/* Wie scharf ein Foto die Stelle sieht: Breite einer Winkelspalte im
   Bild gegenüber der Mitte (1 vorn, 0 am Rand) */
function schaerfeBei(r, psi) {
  const c = Math.cos(psi);
  if (c <= r) return 0;
  const Z = 1 - r * c;
  return (c - r) * (1 - r) / (Z * Z);
}

/* Pixel des Fotos → (s, φ) auf der Flasche (vordere Seite) */
function rueckabbilden(foto, rho, f, R, kb, htop, phi, x, y) {
  const Hm = R.Hm;
  const [xr, yr] = anwenden(Hm, x, y);
  const u = (xr - foto.cx) / f, v = (yr - foto.cy) / f;
  let s = (v * (1 - kb) - htop) / kb, psi = 0;
  for (let it = 0; it < 4; it++) {
    const r = rho(s) * kb;
    const C = Math.max(-1, Math.min(1, u / (r * Math.sqrt(1 + u * u))));
    psi = Math.asin(C) - Math.atan(u);
    const Z = 1 - r * Math.cos(psi);
    s = (v * Z - htop) / kb;
  }
  return [s, psi + phi];
}


/* ── 6. Fotos zueinander ───────────────────────────────────── */

/* Grobe Abwicklung eines Fotos zum Vergleichen: Helligkeit über
   (s, ψ), dazu wie gut die Stelle zu sehen ist. */
function grobAbwickeln(foto, rho, f, uBoden) {
  const nc = 2 * Math.round(PSI_MAX * ABGLEICH_R) + 1, nr = Math.round((uBoden + 0.1) * ABGLEICH_R) + 1;
  const g = new Float32Array(nc * nr), rel = new Float32Array(nc * nr);
  const W = foto.W, H = foto.H;
  for (let i = 0; i < nr; i++) {
    const s = i / ABGLEICH_R;
    const r = rho(s) * foto.kb;
    for (let j = 0; j < nc; j++) {
      const psi = (j - (nc - 1) / 2) / ABGLEICH_R;
      const p = abbilden(foto, rho, f, s, psi);
      const o = i * nc + j;
      if (!p) { g[o] = NaN; continue; }
      g[o] = bilinear(foto.g, W, H, p[0], p[1]);
      rel[o] = schaerfeBei(r, psi);
    }
  }
  return { g, rel, nc, nr };
}

/* Markante Punkte (Harris) und ihre Ausschnitte, in der Helligkeit
   genormt (Licht und Schatten unterscheiden sich von Foto zu Foto). */
const PATCH = 9;          /* Ausschnitt 19 × 19 */
/* Helligkeit örtlich genormt (Licht und Schatten unterscheiden sich
   von Foto zu Foto) und leicht geglättet, dazu der örtliche Kontrast —
   für Ecken und Ausschnitte wie für die dichte Korrelation. */
function normiert(ab) {
  const { nc, nr } = ab, n = nc * nr;
  const g0 = new Float32Array(n);
  for (let i = 0; i < n; i++) { const v = ab.g[i]; g0[i] = v === v ? v : 0; }
  const m = gauss(g0, nc, nr, 4);
  const hp = new Float32Array(n), hp2 = new Float32Array(n);
  for (let i = 0; i < n; i++) { hp[i] = g0[i] - m[i]; hp2[i] = hp[i] * hp[i]; }
  const v2 = gauss(hp2, nc, nr, 4);
  const nrm = new Float32Array(n), kontrast = new Float32Array(n);
  for (let i = 0; i < n; i++) { kontrast[i] = Math.sqrt(v2[i]); nrm[i] = hp[i] / (kontrast[i] + 3); }
  return { glatt: gauss(nrm, nc, nr, 0.8), kontrast };
}

function merkmale(ab) {
  const { nc, nr, rel } = ab;
  const { glatt, kontrast } = normiert(ab);
  const ixx = new Float32Array(nc * nr), iyy = new Float32Array(nc * nr), ixy = new Float32Array(nc * nr);
  for (let y = 1; y < nr - 1; y++) {
    for (let x = 1; x < nc - 1; x++) {
      const o = y * nc + x;
      const gx = (glatt[o + 1] - glatt[o - 1]) / 2, gy = (glatt[o + nc] - glatt[o - nc]) / 2;
      ixx[o] = gx * gx; iyy[o] = gy * gy; ixy[o] = gx * gy;
    }
  }
  const a = gauss(ixx, nc, nr, 1.5), b = gauss(iyy, nc, nr, 1.5), c = gauss(ixy, nc, nr, 1.5);
  const antwort = new Float32Array(nc * nr);
  for (let i = 0; i < antwort.length; i++) antwort[i] = a[i] * b[i] - c[i] * c[i] - 0.04 * (a[i] + b[i]) ** 2;
  /* je Zelle 12 × 12 der stärkste Punkt, nur gut sichtbare Stellen */
  const rand = PATCH + 2, zelle = 12, kand = [];
  for (let cy = rand; cy < nr - rand; cy += zelle) {
    for (let cx = rand; cx < nc - rand; cx += zelle) {
      let best = -1, bv = 0;
      for (let y = cy; y < Math.min(nr - rand, cy + zelle); y++) {
        for (let x = cx; x < Math.min(nc - rand, cx + zelle); x++) {
          const o = y * nc + x;
          if (rel[o] < 0.2 || !Number.isFinite(ab.g[o])) continue;
          if (antwort[o] > bv) { bv = antwort[o]; best = o; }
        }
      }
      if (best >= 0) kand.push([bv, best]);
    }
  }
  kand.sort((p, q) => q[0] - p[0]);
  /* nur deutliche Ecken (Schrift, Etikettränder) — nicht das Rauschen
     im dunklen Glas */
  const schwelle = kand.length ? 0.04 * kand[Math.floor(kand.length * 0.02)][0] : 0;
  const punkte = [];
  const D = (2 * PATCH + 1) ** 2;
  for (const [v, o] of kand.slice(0, 1500)) {
    if (v < schwelle) break;
    const x = o % nc, y = Math.floor(o / nc);
    const d = new Float32Array(D);
    let s = 0, k = 0;
    for (let dy = -PATCH; dy <= PATCH; dy++) for (let dx = -PATCH; dx <= PATCH; dx++) { const w = glatt[(y + dy) * nc + x + dx]; d[k++] = w; s += w; }
    s /= D;
    let q = 0;
    for (let i = 0; i < D; i++) { d[i] -= s; q += d[i] * d[i]; }
    q = Math.sqrt(q) || 1;
    for (let i = 0; i < D; i++) d[i] /= q;
    /* Unterpixel: Schwerpunkt der Antwort im 3 × 3 */
    let sx = 0, sy = 0, sw = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const w = Math.max(0, antwort[(y + dy) * nc + x + dx]); sx += w * dx; sy += w * dy; sw += w; }
    punkte.push({ x: x + (sw ? sx / sw : 0), y: y + (sw ? sy / sw : 0), d, hals: y < 1.9 * ABGLEICH_R });
  }
  /* Halspunkte mit einem Doppelgänger im selben Bild (Logos und
     Schrift, die sich rund um die Halsschleife wiederholen) taugen
     nicht zum Vergleichen: Sie passen in jedem anderen Foto an mehreren
     Stellen. Am Bauch nicht — dort sähen sich Buchstaben zu oft
     ähnlich, und Wiederholungen rundum sind selten. */
  const eindeutig = punkte.filter((a) => {
    if (!a.hals) return true;
    for (const b of punkte) {
      if (b === a || Math.abs(b.y - a.y) > 0.35 * ABGLEICH_R || Math.abs(b.x - a.x) < 12) continue;
      let s = 0;
      for (let t = 0; t < a.d.length; t++) s += a.d[t] * b.d[t];
      if (s > 0.8) return false;
    }
    return true;
  });
  eindeutig.glatt = glatt;
  eindeutig.kontrast = kontrast;
  return eindeutig;
}

/* Dicht nachmessen: Mit der Verschiebung eines Paars bekannt, wird im
   Überlappungsbereich auf einem Raster jeder Ausschnitt mit Struktur
   (Schrift, Ränder) im anderen Foto in der Nähe gesucht —
   normierte Korrelation, auf Bruchteile genau. Das ergibt ein
   Vielfaches der markanten Punkte und trägt den Ausgleich. */
function dichterAbgleich(abA, abB, pa, pb, dx, dyBei, SX = 5, SY = 9, schritt = 7) {
  const { nc, nr } = abA;
  const P = 7, n = (2 * P + 1) ** 2;
  const A = pa.glatt, Bb = pb.glatt, KA = pa.kontrast;
  /* Summen über Ausschnitte von B aus Summenbildern: Je Stelle bleibt
     nur das Kreuzprodukt zu rechnen */
  if (!pb.summen) {
    const S = new Float64Array((nc + 1) * (nr + 1)), S2 = new Float64Array((nc + 1) * (nr + 1));
    for (let y = 0; y < nr; y++) {
      let z = 0, z2 = 0;
      for (let x = 0; x < nc; x++) {
        const v = Bb[y * nc + x]; z += v; z2 += v * v;
        const o = (y + 1) * (nc + 1) + x + 1;
        S[o] = S[o - nc - 1] + z; S2[o] = S2[o - nc - 1] + z2;
      }
    }
    pb.summen = { S, S2 };
  }
  const { S, S2 } = pb.summen;
  const kasten = (T, x, y) => {
    const x0 = x - P, x1 = x + P + 1, y0 = (y - P) * (nc + 1), y1 = (y + P + 1) * (nc + 1);
    return T[y1 + x1] - T[y1 + x0] - T[y0 + x1] + T[y0 + x0];
  };
  const treffer = [];
  let sa = 0, va = 0;
  const ncc = (xa, ya, xb, yb) => {
    let s = 0;
    for (let j = -P; j <= P; j++) {
      const oa = (ya + j) * nc + xa, ob = (yb + j) * nc + xb;
      for (let i = -P; i <= P; i++) s += A[oa + i] * Bb[ob + i];
    }
    const sb = kasten(S, xb, yb), vb = kasten(S2, xb, yb) - sb * sb / n;
    return vb > 1e-6 ? (s - sa * sb / n) / Math.sqrt(va * vb) : -1;
  };
  for (let ya = P + SY + 1; ya < nr - P - SY - 1; ya += schritt) {
    for (let xa = P + 1; xa < nc - P - 1; xa += schritt) {
      const o = ya * nc + xa;
      if (abA.rel[o] < 0.25 || KA[o] < 5) continue;
      const xb0 = Math.round(xa - dx), yb0 = Math.round(ya - dyBei(ya));
      if (xb0 - SX - P < 1 || xb0 + SX + P > nc - 2 || yb0 - SY - P < 1 || yb0 + SY + P > nr - 2) continue;
      if (abB.rel[yb0 * nc + xb0] < 0.25 || !Number.isFinite(abB.g[yb0 * nc + xb0])) continue;
      let saa = 0;
      sa = 0;
      for (let j = -P; j <= P; j++) for (let i = -P; i <= P; i++) { const u = A[(ya + j) * nc + xa + i]; sa += u; saa += u * u; }
      va = saa - sa * sa / n;
      if (!(va > 1e-6)) continue;
      const w = 2 * SX + 1, feld = new Float32Array(w * (2 * SY + 1));
      let best = -2, bi = -1;
      for (let j = -SY; j <= SY; j++) for (let i = -SX; i <= SX; i++) {
        const v = ncc(xa, ya, xb0 + i, yb0 + j);
        feld[(j + SY) * w + i + SX] = v;
        if (v > best) { best = v; bi = (j + SY) * w + i + SX; }
      }
      if (best < 0.8) continue;
      const bx = bi % w, by = Math.floor(bi / w);
      if (bx === 0 || bx === w - 1 || by === 0 || by === 2 * SY) continue;
      /* eindeutig: kein zweites Maximum fast so hoch weiter weg */
      let zweit = -2;
      for (let k = 0; k < feld.length; k++) {
        const kx = k % w, ky = Math.floor(k / w);
        if (Math.abs(kx - bx) + Math.abs(ky - by) > 2) zweit = Math.max(zweit, feld[k]);
      }
      if (zweit > best - 0.08) continue;
      const fein = (l, m, r) => { const d = l - 2 * m + r; return d < 0 ? Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d)) : 0; };
      const ox = fein(feld[bi - 1], best, feld[bi + 1]), oy = fein(feld[bi - w], best, feld[bi + w]);
      treffer.push([xa, ya, xb0 + bx - SX + ox, yb0 + by - SY + oy]);
    }
  }
  return treffer;
}

/* Zwei Fotos vergleichen: Kandidaten nach Ausschnitt (bester deutlich
   vor dem zweitbesten, Höhe ±0,35 Bauchradien), dann Gruppen gleicher
   Verschiebung — bis zu drei, denn Wiederholungen (Logos rund um die
   Halsschleife) ergeben Scheingruppen; welche stimmt, entscheidet erst
   das Gesamtbild. */
function paarAbgleich(pa, pb) {
  const DY = Math.round(0.35 * ABGLEICH_R);
  const nachY = [...pb.keys()].sort((i, j) => pb[i].y - pb[j].y);
  const ysB = nachY.map((i) => pb[i].y);
  const kand = [];
  for (let i = 0; i < pa.length; i++) {
    const a = pa[i];
    let lo = 0, hi = ysB.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (ysB[m] < a.y - DY) lo = m + 1; else hi = m; }
    let b1 = -2, b2 = -2, j1 = -1;
    for (let k = lo; k < ysB.length && ysB[k] <= a.y + DY; k++) {
      const b = pb[nachY[k]];
      let s = 0;
      for (let t = 0; t < a.d.length; t++) s += a.d[t] * b.d[t];
      if (s > b1) { b2 = b1; b1 = s; j1 = nachY[k]; } else if (s > b2) b2 = s;
    }
    /* Halspunkte zählen halb: Dort wiederholt sich oft, was das
       Selbstähnlichkeits-Sieb nicht erwischt */
    if (j1 >= 0 && b1 > 0.6 && (1 - b1) < 0.7 * (1 - b2)) kand.push([i, j1, a.x - pb[j1].x, a.y - pb[j1].y, a.hals ? 0.5 : 1]);
  }
  const gruppen = [];
  let rest = kand;
  for (let g = 0; g < 3 && rest.length >= 4; g++) {
    let best = null;
    for (const [, , dx, dy] of rest) {
      let n = 0;
      for (const k of rest) if (Math.abs(k[2] - dx) < 2.5 && Math.abs(k[3] - dy) < 9) n += k[4];
      if (!best || n > best.n) best = { n, dx, dy };
    }
    const drin = rest.filter((k) => Math.abs(k[2] - best.dx) < 3 && Math.abs(k[3] - best.dy) < 10);
    if (drin.length < 4) break;
    /* Verschiebung null: Spiegelungen auf dem Glas hängen an der
       Kamera, nicht an der Flasche */
    if (Math.abs(best.dx) > 0.2 * ABGLEICH_R) {
      gruppen.push({ dx: median(drin.map((k) => k[2])), dy: median(drin.map((k) => k[3])), paare: drin.map((k) => [k[0], k[1]]), gewicht: drin.reduce((s, k) => s + k[4], 0), koerper: drin.filter((k) => k[4] === 1).length });
    }
    rest = rest.filter((k) => !(Math.abs(k[2] - best.dx) < 6 && Math.abs(k[3] - best.dy) < 12));
  }
  return gruppen;
}

const wickeln = (a) => a - 2 * Math.PI * Math.round(a / (2 * Math.PI));


/* Drehwinkel aller Fotos aus den Kandidaten der Paare: Gesucht ist
   die Auswahl mit den meisten übereinstimmenden Treffern, bei der die
   Fotos rundum reichen (keine Lücke über gut 150°). Probiert werden
   Bäume aus den Kandidaten (zufällig, nach Trefferzahl gewichtet, dazu
   der Baum der stärksten) — bei höchstens acht Fotos und drei
   Kandidaten je Paar reicht das, um Scheingruppen auszusortieren: Die
   wiederholten Logos einer Halsschleife ergeben untereinander
   stimmige Winkel, aber nie einen ganzen Umlauf. */
function winkelWaehlen(n, kanten) {
  /* Gruppen nur aus Halspunkten tragen zuerst keinen Baum: Ein Siegel
     oder Schriftzug, der sich auf der Kapsel gegenüber wiederholt
     (Teynac: zweimal, 180° auseinander), ist nie zweimal im selben Foto
     zu sehen und passt dann zwischen vielen Paaren — alle um 180°
     verdreht und untereinander stimmig. Sie zählen weiter als Stütze;
     nur wenn ohne sie nichts geht, verbinden sie auch. */
  const mitKoerper = kanten.filter((k) => k.koerper > 0);
  return (mitKoerper.length && mitKoerper.length < kanten.length && winkelAus(n, kanten, mitKoerper)) || winkelAus(n, kanten, kanten);
}

function winkelAus(n, kanten, baumKanten) {
  if (!kanten.length) return null;
  let zufall = 12345;
  const rnd = () => { zufall = (zufall * 1664525 + 1013904223) >>> 0; return zufall / 4294967296; };
  const bewerten = (phi, ks) => {
    /* je Paar zählt die stärkste passende Gruppe */
    const gestuetzt = new Map();
    for (const k of ks) {
      if (Math.abs(wickeln(phi[k.b] - phi[k.a] - k.dphi)) > 0.1) continue;
      const schl = k.a * 64 + k.b;
      gestuetzt.set(schl, Math.max(gestuetzt.get(schl) || 0, k.gewicht));
    }
    let punkte = 0;
    for (const v of gestuetzt.values()) punkte += v;
    /* Zwei Fotos, die sich weit überlappen müssten, aber nichts
       Gemeinsames zeigen: so liegen sie nicht. (Erst unter 1 rad:
       Benachbarte Fotos einer Sechserreihe liegen gut 1 rad auseinander,
       und ein Paar mit wenig Schrift im Überlapp findet keine Gruppe.) */
    for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) {
      if (Math.abs(wickeln(phi[b] - phi[a])) < 1 && !gestuetzt.has(a * 64 + b)) punkte -= 8;
    }
    const w = phi.map((v) => ((v % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)).sort((a, b) => a - b);
    let luecke = 0;
    for (let i = 0; i < n; i++) luecke = Math.max(luecke, ((w[(i + 1) % n] - w[i]) + 2 * Math.PI) % (2 * Math.PI) || 2 * Math.PI);
    return { punkte, luecke };
  };
  /* Wald aus den Kandidaten: je Teil (Fotos, die über Gruppen
     verbunden sind) Winkel gegenüber seinem ersten Foto */
  const wald = (ks, zufaellig) => {
    const phi = new Array(n).fill(NaN), teil = new Array(n).fill(-1);
    let nt = 0;
    for (let schritt = 0; schritt < n; schritt++) {
      const moeglich = ks.filter((k) => (teil[k.a] >= 0) !== (teil[k.b] >= 0));
      if (!moeglich.length) { const i = teil.indexOf(-1); teil[i] = nt++; phi[i] = 0; continue; }
      let k;
      if (zufaellig) {
        const summe = moeglich.reduce((s, x) => s + x.gewicht, 0);
        let z = rnd() * summe;
        k = moeglich.find((x) => (z -= x.gewicht) <= 0) || moeglich[moeglich.length - 1];
      } else k = moeglich.reduce((p, q) => (q.gewicht > p.gewicht ? q : p));
      if (teil[k.a] >= 0) { phi[k.b] = phi[k.a] + k.dphi; teil[k.b] = teil[k.a]; } else { phi[k.a] = phi[k.b] - k.dphi; teil[k.a] = teil[k.b]; }
    }
    return { phi, teil, nt };
  };
  /* Die Verschiebungen in der groben Abwicklung sind nur so genau wie
     die geschätzte Gestalt: Ein, zwei Prozent zu viel oder zu wenig je
     Paar summieren sich rundum zu mehr als der Toleranz. Darum auch
     mit etwas gestauchten und gedehnten Winkeln. */
  let best = null;
  for (const mass of [1, 0.98, 1.02, 0.96, 1.04]) {
    const ks = kanten.map((k) => ({ ...k, dphi: k.dphi * mass }));
    const kb = baumKanten.map((k) => ({ ...k, dphi: k.dphi * mass }));
    for (let v = 0; v < 600; v++) {
      const { phi, nt } = wald(kb, v > 0);
      if (nt > 1) break;
      const { punkte, luecke } = bewerten(phi, ks);
      if (luecke > 2.65) continue;
      /* bei gleicher Stütze ungestaucht */
      const wert = punkte - 50 * Math.abs(mass - 1);
      if (!best || wert > best.wert + 1e-9) best = { phi, punkte, wert, mass };
    }
  }
  const ganz = best;
  /* Umlauf aus Teilen: Zwischen Vorder- und Rückenetikett ist oft nur
     blankes Glas, ohne eine einzige gemeinsame Stelle (die Spiegelungen
     hängen an der Kamera). Dann werden die Teile so aneinandergesetzt,
     dass die Fotos rundum möglichst gleichmäßig liegen — gleichmäßig
     weitergedreht, wie es die Anleitung verlangt; auf die Reihenfolge
     der Fotos kommt es nicht an. Probiert werden Wälder ohne einzelne
     Gruppen — aber keine deutliche Gruppe darf dabei übergangen werden:
     Je Paar muss die stärkste passen, wenn sie mindestens ein Fünftel
     der stärksten überhaupt ausmacht (sonst lägen etwa drei Fotos von
     derselben Seite plötzlich rundum).
     Das gilt auch, wenn sich ein Umlauf aus Gruppen findet: Verbindet
     nur eine Scheingruppe die beiden Seiten (Teynac: Kapselsiegel,
     Spiegelungen), liegt ein Foto falsch, und eine starke Gruppe passt
     nicht mehr. Der Wald gewinnt, wenn er deutlich mehr Stütze hat. */
  best = null;
  const staerkste = new Map();
  for (const k of baumKanten) { const s = k.a * 64 + k.b; if (!staerkste.has(s) || k.gewicht > staerkste.get(s).gewicht) staerkste.set(s, k); }
  const maxGewicht = Math.max(...baumKanten.map((k) => k.gewicht));
  const pflicht = [...staerkste.values()].filter((k) => k.gewicht >= Math.max(8, 0.2 * maxGewicht));
  const gesetzt = new Map();
  for (const mass of [1, 0.98, 1.02, 0.96, 1.04]) {
    const ks0 = kanten.map((k) => ({ ...k, dphi: k.dphi * mass }));
    const kb0 = baumKanten.map((k) => ({ ...k, dphi: k.dphi * mass }));
    for (let v = 0; v < 400; v++) {
      const ks = v === 0 ? kb0 : kb0.filter(() => rnd() > 0.3);
      const { phi: rel, teil, nt } = wald(ks, v > 1);
      if (nt > 3) continue;
      let phi = rel;
      if (nt > 1) {
        /* dieselben Teile mit denselben Winkeln kommen oft wieder */
        const schl = teil.join(",") + "|" + rel.map((p) => Math.round(p * 1000)).join(",");
        if (!gesetzt.has(schl)) gesetzt.set(schl, gleichmaessigSetzen(rel, teil, nt));
        phi = gesetzt.get(schl);
      }
      if (!phi || pflicht.some((k) => Math.abs(wickeln(phi[k.b] - phi[k.a] - mass * k.dphi)) > 0.1)) continue;
      const { punkte, luecke } = bewerten(phi, ks0);
      if (luecke > 2.65) continue;
      const wert = punkte - 50 * Math.abs(mass - 1);
      if (!best || wert > best.wert + 1e-9) best = { phi, punkte, wert, mass };
    }
  }
  if (ganz && (!best || best.wert <= ganz.wert + 8)) return { phi: ganz.phi, mass: ganz.mass, punkte: ganz.punkte, teil: new Array(n).fill(0) };
  if (!best) return null;
  /* Teile neu aus allen Gruppen, die zur Wahl passen: Ein Foto, das so
     gesetzt wurde, kann dabei doch passend liegen */
  const teileVon = (phi, mass) => {
    const teil = [...Array(n).keys()];
    const wurzel = (i) => (teil[i] === i ? i : (teil[i] = wurzel(teil[i])));
    for (const k of kanten) {
      if (Math.abs(wickeln(phi[k.b] - phi[k.a] - mass * k.dphi)) <= 0.1) teil[wurzel(k.a)] = wurzel(k.b);
    }
    const nummer = new Map();
    for (let i = 0; i < n; i++) { const w = wurzel(i); if (!nummer.has(w)) nummer.set(w, nummer.size); }
    return teil.map((_, i) => nummer.get(wurzel(i)));
  };
  /* Einrasten: Gleichmäßig gesetzt ist nur eine Annahme — fehlt ein
     Foto (fünf statt sechs, eine Lücke von 120°), liegen die Teile bis
     40° daneben. Zwischen den Teilen gibt es oft doch schwache Gruppen:
     solche nur aus Halspunkten (die keinen Baum tragen dürfen, siehe
     winkelWaehlen) oder zu schwache. Je Paar von Teilen werden ihre
     Verschiebungen gesammelt (einig auf 0,1 rad) und nach Stütze und
     Nähe zur Annahme bewertet (Gewicht × exp(−½(Δ/0,6)²), höchstens
     60° Abstand); die beste rastet ein, wenn sie genug Stütze hat.
     Danach werden die übrigen Teile neu gleichmäßig gesetzt.
     Steht die Kapselschrift zweimal rundum, sind die meisten Gruppen
     zwischen den Teilen um 180° verdreht. Um 180° gedreht stützen sie
     eine richtige Gruppe, mit der sie übereinstimmen (halbes Gewicht),
     aber allein rasten sie nicht ein: Die Wiederholung liegt nicht
     immer genau bei 180° (ein Bild auf Vorder- und Rückenetikett, beim
     Teynac 194°), und ein dreimal wiederholtes Muster (Wittmann, Adler)
     gäbe ±60°. Dann bleiben die Teile getrennt, und die Brücken nach
     dem ersten Ausgleich suchen in weitem Fenster. */
  let phi = best.phi;
  for (let runde = 0; runde < n; runde++) {
    const teil = teileVon(phi, best.mass), nt = Math.max(...teil) + 1;
    if (nt <= 1) break;
    const kand = new Map();
    for (const k of kanten) {
      const ta = teil[k.a], tb = teil[k.b];
      if (ta === tb) continue;
      /* um wie viel der Teil mit der höheren Nummer zu drehen wäre,
         damit die Gruppe passt */
      let delta = wickeln(phi[k.a] + best.mass * k.dphi - phi[k.b]), gedreht = false;
      if (ta > tb) delta = -delta;
      if (Math.abs(delta) > Math.PI / 3) { delta = wickeln(delta + Math.PI); gedreht = true; }
      if (Math.abs(delta) > Math.PI / 3) continue;
      const schl = Math.min(ta, tb) * 64 + Math.max(ta, tb);
      if (!kand.has(schl)) kand.set(schl, []);
      kand.get(schl).push({ delta, gewicht: k.gewicht, gedreht });
    }
    let wahl = null;
    for (const [schl, liste] of kand) {
      for (const c of liste) {
        if (c.gedreht) continue;
        const einig = liste.filter((x) => Math.abs(wickeln(x.delta - c.delta)) < 0.1);
        const direkt = einig.filter((x) => !x.gedreht);
        const gd = direkt.reduce((sm, x) => sm + x.gewicht, 0);
        const delta = direkt.reduce((sm, x) => sm + x.delta * x.gewicht, 0) / gd;
        const gewicht = gd + 0.5 * einig.reduce((sm, x) => sm + (x.gedreht ? x.gewicht : 0), 0);
        const wert = gewicht * Math.exp(-0.5 * (delta / 0.6) ** 2);
        if (gewicht >= 4 && wert >= 2.5 && (!wahl || wert > wahl.wert)) wahl = { tb: schl % 64, delta, wert };
      }
    }
    if (!wahl) break;
    const neu = phi.map((p, i) => (teil[i] === wahl.tb ? wickeln(p + wahl.delta) : p));
    const t2 = teileVon(neu, best.mass), n2 = Math.max(...t2) + 1;
    const gesetzt2 = n2 > 1 ? gleichmaessigSetzen(neu, t2, n2) : neu;
    const { luecke } = bewerten(gesetzt2, kanten.map((k) => ({ ...k, dphi: k.dphi * best.mass })));
    if (luecke > 2.65 || pflicht.some((k) => Math.abs(wickeln(gesetzt2[k.b] - gesetzt2[k.a] - best.mass * k.dphi)) > 0.1)) break;
    phi = gesetzt2.map((p) => wickeln(p - gesetzt2[0]));
  }
  return { phi, mass: best.mass, punkte: best.punkte, teil: teileVon(phi, best.mass) };
}

/* Teile (je mit Winkeln gegenüber ihrem ersten Foto) zu einem Umlauf:
   die Lage der Teile 1, 2 gegenüber Teil 0 so, dass die Abstände
   zwischen den im Kreis benachbarten Fotos möglichst nahe an 2π/n
   liegen (Raster, dann fein). Abstände innerhalb eines Teils liegen
   fest; wie die Teile ineinandergreifen, ergibt sich aus den Winkeln,
   nicht aus der Reihenfolge der Fotos. */
function gleichmaessigSetzen(rel, teil, nt) {
  const n = rel.length, voll = 2 * Math.PI, soll = voll / n;
  const mod = (a) => ((a % voll) + voll) % voll;
  const kosten = (lage) => {
    const w = rel.map((p, i) => mod(p + lage[teil[i]])).sort((a, b) => a - b);
    let k = 0;
    for (let i = 0; i < n; i++) k += ((i + 1 < n ? w[i + 1] - w[i] : w[0] + voll - w[i]) - soll) ** 2;
    return k;
  };
  const lage = new Array(nt).fill(0);
  for (const schritt of [Math.PI / 90, Math.PI / 1800]) {
    for (let runde = 0; runde < 3; runde++) {
      for (let t = 1; t < nt; t++) {
        const grob = schritt > 0.01, mitte = grob ? Math.PI : lage[t], weite = grob ? Math.PI : Math.PI / 90;
        let bw = Infinity, bl = lage[t];
        for (let x = mitte - weite; x < mitte + weite; x += schritt) {
          lage[t] = x;
          const k = kosten(lage);
          if (k < bw) { bw = k; bl = x; }
        }
        lage[t] = bl;
      }
    }
  }
  return rel.map((p, i) => wickeln(p + lage[teil[i]]));
}


/* ── 7. Ausgleich ──────────────────────────────────────────── */

/* Kleines Gleichungssystem (symmetrisch, positiv) lösen */
function loesen(A, b) {
  const n = b.length, M = A.map((r, i) => [...r, b[i]]);
  for (let i = 0; i < n; i++) {
    let p = i;
    for (let k = i + 1; k < n; k++) if (Math.abs(M[k][i]) > Math.abs(M[p][i])) p = k;
    [M[i], M[p]] = [M[p], M[i]];
    const d = M[i][i];
    if (Math.abs(d) < 1e-14) return null;
    for (let k = i + 1; k < n; k++) {
      const f = M[k][i] / d;
      if (f) for (let j = i; j <= n; j++) M[k][j] -= f * M[i][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
}

function ausgleichen(fotos, messungen, rho, start) {
  const n = fotos.length;
  /* p = [f, kb_0 … kb_n−1, htop_0 … htop_n−1, φ_1 … φ_n−1 (φ_0 = 0),
     Kippung_0 … Kippung_n−1]. Die Kippung dreht das entzerrte Bild
     um seine waagerechte Achse: Die Neigung der Kamera folgt aus dem
     Zusammenlaufen der Bauchkanten, und das ist bei einer schmalen
     Flasche nur ein, zwei Pixel — ein Fehler dort verzerrt Hals gegen
     Bauch, und ohne diese Freiheit wiche die Brennweite aus. */
  const auspacken = (p) => ({ f: p[0], kb: p.slice(1, 1 + n), ht: p.slice(1 + n, 1 + 2 * n), phi: [0, ...p.slice(1 + 2 * n, 3 * n)], kipp: p.slice(3 * n, 4 * n) });
  const f0 = start.fVorgabe || start.f;
  const kb0 = (f, i) => fotos[i].wBauch / Math.sqrt(f * f + fotos[i].wBauch ** 2);
  /* Reste: je Messung zwei (Höhe, Winkel), dann die Vorgaben. Mit
     `teil` = [Ausgangsreste, Foto] werden nur die Messungen dieses
     Fotos neu gerechnet (für die Ableitungen nach seinen Größen). */
  const proFoto = Array.from({ length: n }, () => []);
  messungen.forEach(([a, , , b], k) => { proFoto[a].push(k); if (b !== a) proFoto[b].push(k); });
  const reste = (p, gewichte = null, teil = null) => {
    const { f, kb, ht, phi, kipp } = auspacken(p);
    const R = fotos.map((m, i) => (teil && i !== teil[1] ? null : homographien(f, m.cx, m.cy, rektifizierung(f, m.cx, m.cy, m.flucht, m.achsPunkt, kipp[i]))));
    const nM = 2 * messungen.length;
    let r;
    const messe = (k) => {
      const [a, xa, ya, b, xb, yb] = messungen[k];
      const [sa, pa] = rueckabbilden(fotos[a], rho, f, R[a] || teil[2][a], kb[a], ht[a], phi[a], xa, ya);
      const [sb, pb] = rueckabbilden(fotos[b], rho, f, R[b] || teil[2][b], kb[b], ht[b], phi[b], xb, yb);
      const w = gewichte ? gewichte[k] : 1;
      r[2 * k] = (sa - sb) * T * w; r[2 * k + 1] = wickeln(pa - pb) * T * w;
    };
    if (teil) { r = teil[0].slice(); for (const k of proFoto[teil[1]]) messe(k); } else { r = new Array(nM); for (let k = 0; k < messungen.length; k++) messe(k); }
    r.length = nM;
    for (let i = 0; i < n; i++) r.push((kb[i] / kb0(f, i) - 1) / 0.02);
    for (let i = 0; i < n; i++) r.push(kipp[i] / 0.05);
    /* schwach an die Ausgangslage: legt nur fest, was keine Messung
       festlegt (Teile ohne gemeinsame Stellen, siehe winkelWaehlen) —
       gegen Hunderte Treffer zählt es nicht */
    for (let i = 0; i < n; i++) r.push((ht[i] - start.ht[i]) / 0.05);
    for (let i = 1; i < n; i++) r.push(wickeln(phi[i] - start.phi[i]) / 0.3);
    r.push((f / f0 - 1) / start.fStreuung);
    r.R = R;
    return r;
  };
  /* zu welchem Foto ein Parameter gehört (f: allen) */
  const fotoVon = (j) => (j === 0 ? -1 : j <= n ? j - 1 : j <= 2 * n ? j - n - 1 : j < 3 * n ? j - 2 * n : j - 3 * n);
  const lm = (p, gewichte) => {
    let lambda = 1e-3, r = reste(p, gewichte), kosten = r.reduce((s, v) => s + v * v, 0);
    for (let it = 0; it < 30; it++) {
      const J = [];
      for (let j = 0; j < p.length; j++) {
        const h = j === 0 ? p[0] * 1e-5 : 1e-6;
        const q = [...p]; q[j] += h;
        const i = fotoVon(j);
        const r2 = i < 0 ? reste(q, gewichte) : reste(q, gewichte, [r, i, r.R]);
        J.push(r2.map((v, k) => (v - r[k]) / h));
      }
      const m = p.length;
      const A = Array.from({ length: m }, () => new Array(m).fill(0)), g = new Array(m).fill(0);
      for (let i = 0; i < m; i++) {
        for (let j = i; j < m; j++) { let s = 0; const a = J[i], b = J[j]; for (let k = 0; k < r.length; k++) s += a[k] * b[k]; A[i][j] = A[j][i] = s; }
        let s = 0; for (let k = 0; k < r.length; k++) s += J[i][k] * r[k]; g[i] = -s;
      }
      let besser = false;
      for (let versuch = 0; versuch < 8; versuch++) {
        const Ad = A.map((row, i) => row.map((v, j) => (i === j ? v * (1 + lambda) + 1e-9 : v)));
        const d = loesen(Ad, g);
        if (!d) { lambda *= 10; continue; }
        const q = p.map((v, i) => v + d[i]);
        const r2 = reste(q, gewichte), k2 = r2.reduce((s, v) => s + v * v, 0);
        if (k2 < kosten) {
          const fein = kosten - k2 < 1e-6 * kosten;
          p = q; r = r2; kosten = k2; lambda = Math.max(1e-7, lambda / 3); besser = true;
          if (fein) return p;
          break;
        }
        lambda *= 8;
      }
      if (!besser) break;
    }
    return p;
  };
  let p = [start.f, ...start.kb, ...start.ht, ...start.phi.slice(1), ...(start.kipp || new Array(n).fill(0))];
  /* robust: Gewichte nach den Resten (weich begrenzt), zweimal */
  let gewichte = new Array(messungen.length).fill(1);
  for (let runde = 0; runde < 3; runde++) {
    p = lm(p, gewichte);
    const r = reste(p);
    gewichte = messungen.map((_, k) => {
      const e = Math.hypot(r[2 * k], r[2 * k + 1]);
      return runde < 2 ? 1 / Math.sqrt(1 + (e / 2) ** 2) ** 0.5 : (e < 5 ? 1 : 0);
    });
  }
  p = lm(p, gewichte);
  const r = reste(p);
  const fehler = [];
  for (let k = 0; k < messungen.length; k++) if (gewichte[k] > 0) fehler.push(Math.hypot(r[2 * k], r[2 * k + 1]));
  return { ...auspacken(p), restMedian: median(fehler), restQ90: fehler.sort((a, b) => a - b)[Math.floor(fehler.length * 0.9)] || NaN, genutzt: fehler.length };
}


/* ── 8. Zusammensetzen ─────────────────────────────────────── */

/* Versatz zwischen zwei Fotos entlang ihrer Naht: Ein Streifen um den
   Nahtpfad wird aus beiden Fotos gelesen (jede zweite Zeile und
   Spalte); in Abschnitten von gut 0,3 Bauchradien Höhe wird der
   Ausschnitt von a in b gesucht (normierte Korrelation, erst grob,
   dann fein). Höhe und Seite getrennt: Ein waagerechter Kapselrand
   legt nur die Höhe fest, ein senkrechter Etikettrand nur die Seite.
   Gezählt wird je Richtung nur ein deutlicher, eindeutiger Treffer mit
   Struktur in beiden Fotos — im glatten Glas gibt es keinen. Bis
   `sHals` (Beginn der Schulter) gilt ein Versatz für den ganzen Hals.
   Liefert je Zeile oy, ox (Zeilen und Spalten der Textur):
   a(y, c) ≈ b(y + oy, c + ox). */
function nahtVersatz(nt, farbe, H, TR, sHals = 2, nurHals = false) {
  const hy = Math.max(4, Math.round(0.08 * TR)), hx = Math.max(3, Math.round(0.08 * TR));   /* halbe Ausschnitte in Rasterpunkten (×2 Pixel) */
  const DY = Math.max(4, Math.round(0.15 * TR)), DX = Math.max(3, Math.round(0.13 * TR));  /* Suchweite in Rasterpunkten */
  /* nur der Hals: Streifen bis knapp unter die Schulter */
  const nz = nurHals ? Math.min(Math.ceil(H / 2), Math.round(sHals * TR / 2) + hy + DY + 1) : Math.ceil(H / 2);
  const ns = 2 * (hx + DX) + 1, j0 = hx + DX;
  const lum = (i) => {
    const g = new Float32Array(nz * ns).fill(NaN);
    for (let z = 0; z < nz; z++) {
      const y = 2 * z;
      for (let j = 0; j < ns; j++) {
        const f = farbe(i, y, nt.pfad[Math.min(H - 1, y)] + 2 * (j - j0));
        if (f) g[z * ns + j] = 0.299 * f[0] + 0.587 * f[1] + 0.114 * f[2];
      }
    }
    return g;
  };
  /* Hochpass (minus Mittel über ±4 Rasterpunkte): Glanzlichter auf
     Kapsel und Glas wandern mit der Kamera und sind breit; Schrift,
     Ränder und Rillen sitzen fest und sind schmal */
  const hoch = (g) => {
    const aus = new Float32Array(g.length).fill(NaN), r = 4;
    for (let z = 0; z < nz; z++) {
      for (let j = 0; j < ns; j++) {
        const v = g[z * ns + j];
        if (!Number.isFinite(v)) continue;
        let sm = 0, k = 0;
        for (let dz = -r; dz <= r; dz++) {
          const zz = z + dz;
          if (zz < 0 || zz >= nz) continue;
          for (let dj = -r; dj <= r; dj++) {
            const jj = j + dj;
            if (jj < 0 || jj >= ns) continue;
            const w = g[zz * ns + jj];
            if (Number.isFinite(w)) { sm += w; k++; }
          }
        }
        aus[z * ns + j] = v - sm / k;
      }
    }
    return aus;
  };
  const A = hoch(lum(nt.a)), B = hoch(lum(nt.b));
  const ncc = (z0, oz, ox, schritt) => {
    let sa = 0, sb = 0, saa = 0, sbb = 0, sab = 0, n = 0;
    for (let dz = -hy; dz <= hy; dz += schritt) {
      const za = z0 + dz, zb = za + oz;
      if (za < 0 || zb < 0 || za >= nz || zb >= nz) continue;
      for (let dj = -hx; dj <= hx; dj += schritt) {
        const u = A[za * ns + j0 + dj], v = B[zb * ns + j0 + dj + ox];
        if (!Number.isFinite(u) || !Number.isFinite(v)) continue;
        sa += u; sb += v; saa += u * u; sbb += v * v; sab += u * v; n++;
      }
    }
    if (n < 20) return -1;
    const va = saa - sa * sa / n, vb = sbb - sb * sb / n;
    if (Math.min(va, vb) / n < 16) return -1;                  /* zu wenig Struktur */
    return (sab - sa * sb / n) / Math.sqrt(va * vb);
  };
  /* eindeutiges Maximum eines Profils über die Versätze */
  const eindeutig = (werte, versatz, schwelle) => {
    let bi = 0;
    for (let i = 1; i < werte.length; i++) if (werte[i] > werte[bi]) bi = i;
    let zweit = -1;
    for (let i = 0; i < werte.length; i++) if (Math.abs(versatz[i] - versatz[bi]) > 4) zweit = Math.max(zweit, werte[i]);
    /* am Rand der Suche: der wahre Versatz läge außerhalb — nicht zählen */
    if (bi === 0 || bi === werte.length - 1) return -1;
    return werte[bi] >= schwelle && zweit < werte[bi] - 0.06 ? bi : -1;
  };
  const abstand = Math.max(2, Math.round(0.075 * TR));
  const ozs = [], oxs = [];
  for (let oz = -DY; oz <= DY; oz += 2) ozs.push(oz);
  for (let ox = -DX; ox <= DX; ox += 2) oxs.push(ox);
  const bruch = (l, m, q) => { const d = l - 2 * m + q; return d < 0 ? Math.max(-0.5, Math.min(0.5, 0.5 * (l - q) / d)) : 0; };
  /* fein um (oz, ox): ±2 Rasterpunkte, voller Ausschnitt, Bruchteile;
     `zeilen` mehrere Abschnitte gemeinsam */
  const verfeinern = (zeilen, oz0, ox0) => {
    let fein = null;
    const feld = new Map();
    for (let oz = oz0 - 2; oz <= oz0 + 2; oz++) {
      for (let ox = ox0 - 2; ox <= ox0 + 2; ox++) {
        let sum = 0, k = 0;
        for (const z0 of zeilen) { const v = ncc(z0, oz, ox, 1); if (v > -1) { sum += v; k++; } }
        const v = k ? sum / k : -1;
        feld.set(oz * 1000 + ox, v);
        if (!fein || v > fein.r) fein = { oz, ox, r: v };
      }
    }
    const g = (oz, ox) => feld.get(oz * 1000 + ox) ?? fein.r;
    return {
      r: fein.r,
      oy: 2 * (fein.oz + bruch(g(fein.oz - 1, fein.ox), fein.r, g(fein.oz + 1, fein.ox))),
      ox: 2 * (fein.ox + bruch(g(fein.oz, fein.ox - 1), fein.r, g(fein.oz, fein.ox + 1))),
    };
  };
  const baender = [];
  for (let z0 = hy; z0 < nz - hy; z0 += abstand) {
    if (nurHals && z0 >= Math.round(sHals * TR / 2)) break;
    baender.push({ z0, r: ozs.map((oz) => oxs.map((ox) => ncc(z0, oz, ox, 2))) });
  }
  const auswerten = (r, schwelle) => {
    const proZ = r.map((zeile) => Math.max(...zeile));
    const proX = oxs.map((_, k) => Math.max(...r.map((zeile) => zeile[k])));
    return { iz: eindeutig(proZ, ozs, schwelle), ix: eindeutig(proX, oxs, schwelle) };
  };
  /* Hals und Kapsel sind starr und zeigen viel Glanz: ein Versatz für
     den ganzen Hals. Jeder Abschnitt für sich ist oft zu schwach (die
     Glanzlichter wandern mit der Kamera), aber die besten Versätze der
     Abschnitte stimmen überein, wo Schrift, Rillen und Kapselrand
     liegen — gilt der, auf den sich die meisten Abschnitte einigen. */
  const zHals = Math.round(sHals * TR / 2);
  const halsB = baender.filter((b) => b.z0 < zHals);
  let hals = null;
  const spitzen = [];
  for (const b of halsB) {
    let bi = 0, bk = 0;
    for (let i = 0; i < ozs.length; i++) for (let k = 0; k < oxs.length; k++) if (b.r[i][k] > b.r[bi][bk]) { bi = i; bk = k; }
    const amRand = bi === 0 || bi === ozs.length - 1 || bk === 0 || bk === oxs.length - 1;
    if (!amRand && b.r[bi][bk] >= 0.15) spitzen.push({ z0: b.z0, oz: ozs[bi], ox: oxs[bk], r: b.r[bi][bk] });
  }
  /* je Richtung getrennt: Am Kapselrand stimmt nur die Höhe, an
     senkrechter Schrift nur die Seite */
  const einig = (feld) => {
    let gruppe = [];
    for (const p of spitzen) {
      const g = spitzen.filter((q) => Math.abs(q[feld] - p[feld]) <= 3);
      if (g.length > gruppe.length || (g.length === gruppe.length && g.reduce((t, q) => t + q.r, 0) > gruppe.reduce((t, q) => t + q.r, 0))) gruppe = g;
    }
    return gruppe.length >= Math.max(3, Math.ceil(0.2 * halsB.length)) ? gruppe : null;
  };
  const gz = einig("oz"), gx = einig("ox");
  if (gz || gx) {
    const fz = gz && verfeinern(gz.map((q) => q.z0), Math.round(median(gz.map((q) => q.oz))), Math.round(median(gz.map((q) => q.ox))));
    const fx = gx && verfeinern(gx.map((q) => q.z0), Math.round(median(gx.map((q) => q.oz))), Math.round(median(gx.map((q) => q.ox))));
    hals = { oy: fz ? fz.oy : null, ox: fx ? fx.ox : null, abschnitte: [gz ? gz.length : 0, gx ? gx.length : 0] };
  }
  /* darunter je Abschnitt, streng */
  const mess = [];
  for (const b of baender) {
    if (b.z0 < zHals) continue;
    const { iz, ix } = auswerten(b.r, 0.6);
    if (iz < 0 && ix < 0) continue;
    const kz = iz >= 0 ? iz : b.r.reduce((m, zeile, k) => (zeile[ix] > b.r[m][ix] ? k : m), 0);
    const kx = ix >= 0 ? ix : b.r[kz].reduce((m, v, k) => (v > b.r[kz][m] ? k : m), 0);
    const f = verfeinern([b.z0], ozs[kz], oxs[kx]);
    if (f.r < 0.65) continue;
    mess.push({ y: 2 * b.z0, oy: iz >= 0 ? f.oy : null, ox: ix >= 0 ? f.ox : null });
  }
  /* je Richtung und Zeile: am Hals der eine Versatz, darunter Median
     über benachbarte Abschnitte, dazwischen linear, sonst auf
     0,4 Bauchradien auslaufend */
  const aus = Math.round(0.4 * TR), yHals = 2 * zHals;
  const verlauf = (feldname, halsWert = hals ? hals[feldname] : null) => {
    const pkt = mess.filter((m) => m[feldname] != null).map((m) => [m.y, m[feldname]]);
    const glatt = pkt.map((m, k) => {
      const nb = pkt.slice(Math.max(0, k - 1), k + 2).filter((q) => Math.abs(q[0] - m[0]) <= 5 * abstand);
      return [m[0], median(nb.map((q) => q[1]))];
    });
    if (halsWert != null) {
      const halsPkt = [];
      for (let y = 0; y <= yHals; y += abstand) halsPkt.push([y, halsWert]);
      glatt.unshift(...halsPkt.filter((q) => !glatt.length || q[0] < glatt[0][0]));
    }
    const v = new Float32Array(H);
    if (!glatt.length) return v;
    for (let y = 0; y < H; y++) {
      let k = 0;
      while (k < glatt.length && glatt[k][0] < y) k++;
      if (k === 0) v[y] = glatt[0][1] * Math.max(0, 1 - (glatt[0][0] - y) / aus);
      else if (k === glatt.length) v[y] = glatt[k - 1][1] * Math.max(0, 1 - (y - glatt[k - 1][0]) / aus);
      else {
        const p = glatt[k - 1], q = glatt[k], t = (y - p[0]) / Math.max(1, q[0] - p[0]);
        /* weit auseinander: dazwischen auf null zurück */
        const w = q[0] - p[0] > 2 * aus ? Math.max(0, 1 - Math.min(y - p[0], q[0] - y) / aus) : 1;
        v[y] = (p[1] + (q[1] - p[1]) * t) * w;
      }
    }
    return v;
  };
  return { verlauf, abschnitte: mess.length, hals, yHals };
}


function textur(fotos, geo, rhoGemessen, uBoden, { beiZeile, boeden = null, sHals = 2, teil = null } = {}) {
  /* Halsradius: als Faktor κ auf das gemessene Profil bis zur Schulter
     (siehe unten, „Hals rundum“), darunter auf 1 auslaufend */
  let kappa = 1;
  const rho = (s) => rhoGemessen(s) * (s <= sHals ? kappa : s >= sHals + 0.5 ? 1 : kappa + (1 - kappa) * (s - sHals) / 0.5);
  const n = fotos.length;
  const wB = median(fotos.map((m) => m.wBauch));
  const TR = Math.min(1.1 * wB, (TEXTUR_MAX - 1) / uBoden);
  const B = Math.round(2 * Math.PI * TR), H = Math.round(uBoden * TR) + 1;
  const dphi = 2 * Math.PI / B;
  const { f } = geo;
  const lagen = fotos.map((m, i) => ({ ...m, kb: geo.kb[i], htop: geo.ht[i], phi: geo.phi[i], ...homographien(f, m.cx, m.cy, rektifizierung(f, m.cx, m.cy, m.flucht, m.achsPunkt, geo.kipp ? geo.kipp[i] : 0)) }));
  const reihe = [...lagen.keys()].sort((a, b) => ((lagen[a].phi % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI) - ((lagen[b].phi % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI));
  const phiN = (i) => ((lagen[i].phi % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const sBei = (y) => y / (H - 1) * uBoden;
  /* Farbe eines Fotos an (Zeile, Spalte) */
  const tmp = new Float32Array(3);
  const farbe = (i, y, c) => {
    const L = lagen[i], s = sBei(y);
    const psi = wickeln(c * dphi - L.phi);
    const p = abbilden(L, rho, f, s, psi);
    if (!p) return null;
    bikubisch(L.data, L.W, L.H, p[0], p[1], tmp, 0);
    return tmp;
  };
  const relBei = (i, y, c) => schaerfeBei(rho(sBei(y)) * lagen[i].kb, wickeln(c * dphi - lagen[i].phi));

  /* Nähte: zwischen zwei im Kreis benachbarten Fotos ein Pfad von oben
     nach unten durch den Überlappungsbereich */
  const naehte = [];
  for (let k = 0; k < n; k++) {
    const a = reihe[k], b = reihe[(k + 1) % n];
    const luecke = n === 1 ? 2 * Math.PI : ((phiN(b) - phiN(a)) + 2 * Math.PI) % (2 * Math.PI) || 2 * Math.PI;
    const mitte = phiN(a) + luecke / 2;
    const halb = Math.max(4, Math.round(Math.min(0.42, 0.4 * luecke) / dphi));
    const c0 = Math.round(mitte / dphi) - halb, nb = 2 * halb + 1;
    /* Helligkeit beider Fotos im Band (jede zweite Zeile genügt für den Pfad) */
    const ga = new Float32Array(H * nb), gb = new Float32Array(H * nb), gut = new Float32Array(H * nb);
    for (let y = 0; y < H; y++) {
      for (let j = 0; j < nb; j++) {
        const c = c0 + j, o = y * nb + j;
        const fa = farbe(a, y, c); const la = fa ? 0.299 * fa[0] + 0.587 * fa[1] + 0.114 * fa[2] : 0;
        const fb = farbe(b, y, c); const lb = fb ? 0.299 * fb[0] + 0.587 * fb[1] + 0.114 * fb[2] : 0;
        ga[o] = la; gb[o] = lb;
        gut[o] = Math.min(relBei(a, y, c), relBei(b, y, c));
      }
    }
    const ma = gauss(ga, nb, H, 4), mb = gauss(gb, nb, H, 4);
    const diff = new Float32Array(H * nb);
    for (let i = 0; i < diff.length; i++) diff[i] = Math.abs((ga[i] - ma[i]) - (gb[i] - mb[i]));
    const dg = gauss(diff, nb, H, 1.5);
    const kosten = new Float32Array(H * nb);
    for (let y = 0; y < H; y++) for (let j = 0; j < nb; j++) {
      const o = y * nb + j;
      kosten[o] = dg[o] + 400 * Math.max(0, 0.3 - gut[o]) + 0.12 * Math.abs(j - halb);
    }
    const acc = Float32Array.from(kosten.subarray(0, nb)), zurueck = new Int8Array(H * nb);
    const neu = new Float32Array(nb);
    for (let y = 1; y < H; y++) {
      for (let j = 0; j < nb; j++) {
        let best = acc[j], d = 0;
        if (j > 0 && acc[j - 1] + 6 < best) { best = acc[j - 1] + 6; d = -1; }
        if (j < nb - 1 && acc[j + 1] + 6 < best) { best = acc[j + 1] + 6; d = 1; }
        neu[j] = best + kosten[y * nb + j]; zurueck[y * nb + j] = d;
      }
      acc.set(neu);
    }
    const pfad = new Int32Array(H);
    let j = 0;
    for (let i = 1; i < nb; i++) if (acc[i] < acc[j]) j = i;
    for (let y = H - 1; y >= 0; y--) { pfad[y] = c0 + j; if (y > 0) j += zurueck[y * nb + j]; }
    naehte.push({ a, b, pfad });
  }
  if (beiZeile) beiZeile(0.25);

  /* Ausgleich an den Nähten. Ganz oben und ganz unten trägt das Modell
     nicht mehr genau: Die Treffer liegen vor allem am Bauch, und schon
     ein Prozent im Maßstab eines Fotos verschiebt Kapsel und Boden
     sichtbar (die Kapsel stand an einer Naht doppelt, unten war die
     Abstellfläche zu sehen). Darum je Foto
     - unten: die eigene Bodenkante (bodenVorn) auf die gemeinsame
       Höhe gezogen, auf den untersten 1,5 Bauchradien verteilt;
     - an jeder Naht: der Versatz zwischen beiden Fotos, abschnittsweise
       gemessen, je zur Hälfte ausgeglichen und zur Fotomitte hin
       auslaufend. */
  const rampe0 = Math.max(0, H - 1 - Math.round(1.5 * TR));
  const bodenZeilen = boeden ? boeden.map((sv) => (sv - uBoden) * (H - 1) / uBoden) : null;
  const bodenVersatz = (i, y) => (bodenZeilen ? bodenZeilen[i] * Math.max(0, Math.min(1, (y - rampe0) / Math.max(1, H - 1 - rampe0))) : 0);
  const farbeB = (i, y, c) => farbe(i, y + bodenVersatz(i, y), c);
  /* Hals rundum: Ist der Halsradius im Profil nur um wenige Prozent zu
     klein oder zu groß, liegt an jeder Naht die Halsschrift seitlich
     versetzt, und zwar überall in dieselbe Richtung (rundum fehlt dann
     ein Stück, „Saint-Estè|ne“, oder es steht doppelt). Eine verdrehte
     Kapsel verschiebt dagegen gegenläufig. Darum zuerst κ so, dass die
     seitlichen Versätze am Hals im Mittel verschwinden (Sekanten,
     höchstens drei Schritte), dann den Rest wie unten. */
  const halsMittel = () => {
    const ox = naehte.map((nt) => nahtVersatz(nt, farbeB, H, TR, sHals, true).hals).filter((h) => h && h.ox != null).map((h) => h.ox);
    return ox.length >= Math.max(3, Math.ceil(n / 2)) ? ox.reduce((a, b) => a + b, 0) / ox.length : null;
  };
  {
    const halb = Math.PI / Math.max(3, n);
    let k0 = 1, m0 = halsMittel(), steigung = 2 * TR * Math.tan(halb) * 1.3;
    for (let schritt = 0; schritt < 3 && m0 != null && Math.abs(m0) > 1.5; schritt++) {
      const k1 = Math.max(0.8, Math.min(1.25, k0 - m0 / steigung));
      kappa = k1;
      const m1 = halsMittel();
      if (m1 == null) { kappa = k0; break; }
      if (Math.abs(m1) >= Math.abs(m0)) { kappa = Math.abs(m1) < Math.abs(m0) ? k1 : k0; break; }
      if (Math.abs(k1 - k0) > 1e-4 && Math.abs(m1 - m0) > 1e-3) steigung = Math.max(0.3 * steigung, Math.min(3 * steigung, (m1 - m0) / (k1 - k0)));
      k0 = k1; m0 = m1;
    }
  }
  for (const nt of naehte) Object.assign(nt, nahtVersatz(nt, farbeB, H, TR, sHals));
  /* Am Hals verschieben sich oft ganze Fotos gegeneinander (die Kapsel
     hat sich beim Weiterdrehen mitgedreht, oder ein Foto liegt dort
     etwas höher): Je Foto ein fester Versatz des Halses, so dass die
     Versätze rundum aufgehen (kleinste Quadrate, Mittel null); an den
     Nähten bleibt nur der Rest auszugleichen. So steht die Kapsel
     waagerecht, statt zur Naht hin schräg zu laufen. */
  const halsFest = { oy: new Float64Array(n), ox: new Float64Array(n) };
  for (const feld of ["oy", "ox"]) {
    const gl = naehte.filter((nt) => nt.hals && nt.hals[feld] != null);
    if (!gl.length || n < 2) continue;
    const A = Array.from({ length: n }, () => new Array(n).fill(0)), r = new Array(n).fill(0);
    for (const nt of gl) {
      /* Foto i wird bei y + d_i gelesen: d_b − d_a = Versatz */
      const v = nt.hals[feld];
      A[nt.a][nt.a] += 1; A[nt.b][nt.b] += 1; A[nt.a][nt.b] -= 1; A[nt.b][nt.a] -= 1;
      r[nt.b] += v; r[nt.a] -= v;
    }
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) A[i][j] += 1e-3;   /* Mittel null */
    const d = loesen(A, r);
    if (d) for (let i = 0; i < n; i++) halsFest[feld][i] = d[i];
  }
  for (const nt of naehte) {
    const rest = (feld) => (nt.hals && nt.hals[feld] != null ? nt.hals[feld] - (halsFest[feld][nt.b] - halsFest[feld][nt.a]) : null);
    nt.oy = nt.verlauf("oy", rest("oy"));
    nt.ox = nt.verlauf("ox", rest("ox"));
  }
  const yHals = Math.round(sHals * TR), aus = Math.round(0.4 * TR);
  const halsAnteil = (y) => (y <= yHals ? 1 : Math.max(0, 1 - (y - yHals) / aus));
  const links = new Map(), rechts = new Map();
  for (const nt of naehte) { rechts.set(nt.a, nt); links.set(nt.b, nt); }
  /* Spalte der Fotomitte bzw. der Naht in Zeile y, nahe Spalte c */
  const nahe = (c, ziel) => c + wickeln((ziel - c) * dphi) / dphi;
  const farbeV = (i, y, c) => {
    const h = halsAnteil(y);
    let uy = bodenVersatz(i, y) + h * halsFest.oy[i], ux = h * halsFest.ox[i];
    const yi = Math.max(0, Math.min(H - 1, Math.round(y)));
    const cI = nahe(c, lagen[i].phi / dphi);
    const rn = rechts.get(i), ln = links.get(i);
    if (rn) {
      const cS = nahe(cI, rn.pfad[yi]);
      const w = cS > cI ? Math.max(0, Math.min(1, (c - cI) / (cS - cI))) : 0;
      uy -= 0.5 * rn.oy[yi] * w; ux -= 0.5 * rn.ox[yi] * w;
    }
    if (ln) {
      const cS = nahe(cI, ln.pfad[yi]);
      const w = cS < cI ? Math.max(0, Math.min(1, (cI - c) / (cI - cS))) : 0;
      uy += 0.5 * ln.oy[yi] * w; ux += 0.5 * ln.ox[yi] * w;
    }
    return farbe(i, y + uy, c + ux);
  };
  if (beiZeile) beiZeile(0.3);

  /* Helligkeit an jeder Naht: Verhältnis b/a je Zeile (je Kanal, im
     Logarithmus), entlang der Naht geglättet; beide Seiten treffen
     sich in der Mitte */
  const sigma = Math.max(8, Math.round(0.12 * TR));
  for (const nt of naehte) {
    const lr = [new Float32Array(H), new Float32Array(H), new Float32Array(H)], wt = new Float32Array(H);
    for (let y = 0; y < H; y++) {
      const sa = [0, 0, 0], sb = [0, 0, 0];
      let ok = 0;
      for (let d = -3; d <= 3; d++) {
        const c = nt.pfad[y] + d;
        const fa = farbeV(nt.a, y, c); if (!fa) continue; const A = [fa[0], fa[1], fa[2]];
        const fb = farbeV(nt.b, y, c); if (!fb) continue;
        if (A[0] + A[1] + A[2] < 60 || fb[0] + fb[1] + fb[2] < 60) continue;
        for (let ch = 0; ch < 3; ch++) { sa[ch] += Math.max(1, A[ch]); sb[ch] += Math.max(1, fb[ch]); }
        ok++;
      }
      if (ok >= 3) { for (let ch = 0; ch < 3; ch++) lr[ch][y] = Math.log(sb[ch] / sa[ch]); wt[y] = ok; }
    }
    const glatt = (v) => {
      const zaehler = gauss(Float32Array.from(v, (x, i) => x * wt[i]), 1, H, sigma), nenner = gauss(wt, 1, H, sigma);
      return Float32Array.from(zaehler, (x, i) => (nenner[i] > 1e-3 ? x / nenner[i] : 0));
    };
    nt.log = lr.map(glatt);
  }
  /* Je Foto die Verstärkung quer über seine Fläche: an der linken Naht
     (zum Vorgänger) und an der rechten (zum Nachfolger), dazwischen
     linear */

  const daten = new Uint8ClampedArray(B * H * 4);
  const besitzer = new Int8Array(B * H).fill(-1);
  let gutSumme = 0, gutAnzahl = 0;
  for (let y = 0; y < H; y++) {
    for (const i of reihe) {
      const ln = links.get(i), rn = rechts.get(i);
      if (!ln || !rn) continue;
      const cL = ln.pfad[y], cR0 = rn.pfad[y];
      const cR = cL + ((((cR0 - cL) % B) + B) % B || B);
      for (let c = cL; c < cR; c++) {
        const cc = ((c % B) + B) % B, o = y * B + cc;
        const fa = farbeV(i, y, c);
        if (!fa) continue;
        const t = (c - cL) / Math.max(1, cR - cL);
        besitzer[o] = i;
        for (let ch = 0; ch < 3; ch++) {
          /* links: Seite b der Naht (÷ √Verhältnis), rechts: Seite a (× √Verhältnis) */
          const g = Math.exp(-0.5 * ln.log[ch][y] * (1 - t) + 0.5 * rn.log[ch][y] * t);
          daten[o * 4 + ch] = fa[ch] * g;
        }
        daten[o * 4 + 3] = 255;
        gutSumme += relBei(i, y, c) >= 0.15 ? 1 : 0; gutAnzahl++;
      }
    }
    if (beiZeile && y % 128 === 0) beiZeile(0.3 + 0.6 * y / H);
  }
  /* Schmal überblenden: zwei Spalten beiderseits jeder Naht */
  for (const nt of naehte) {
    for (let y = 0; y < H; y++) {
      const c0 = nt.pfad[y];
      for (let d = -2; d <= 1; d++) {
        const c = c0 + d, cc = ((c % B) + B) % B, o = y * B + cc;
        const fa = farbeV(nt.a, y, c); if (!fa) continue; const A = [fa[0], fa[1], fa[2]];
        const fb = farbeV(nt.b, y, c); if (!fb) continue;
        const w = (d + 2.5) / 4;
        for (let ch = 0; ch < 3; ch++) {
          const ga = Math.exp(0.5 * nt.log[ch][y]), gb = Math.exp(-0.5 * nt.log[ch][y]);
          daten[o * 4 + ch] = A[ch] * ga * (1 - w) + fb[ch] * gb * w;
        }
      }
    }
  }
  glanzMindern(daten, B, H, besitzer, links, rechts, farbeV, relBei, Math.round(1.3 * TR), Math.round(2 * TR), teil);
  /* Ungesehene Stellen (sollte es nicht geben) aus der Nachbarspalte */
  for (let y = 0; y < H; y++) for (let c = 0; c < B; c++) {
    const o = y * B + c;
    if (besitzer[o] >= 0) continue;
    const q = (y * B + ((c + B - 1) % B)) * 4;
    daten[o * 4] = daten[q]; daten[o * 4 + 1] = daten[q + 1]; daten[o * 4 + 2] = daten[q + 2]; daten[o * 4 + 3] = 255;
  }
  return {
    daten, B, H, TR, abdeckung: gutAnzahl ? gutSumme / gutAnzahl : 0, naehte: naehte.map((nt) => nt.pfad[Math.floor(H / 2)]),
    kappa,
    halsFest: { oy: Array.from(halsFest.oy, (v) => Math.round(v * 10) / 10), ox: Array.from(halsFest.ox, (v) => Math.round(v * 10) / 10) },
    ausgleich: naehte.map((nt) => ({ a: nt.a, b: nt.b, spalte: nt.pfad[Math.round(0.8 * TR)], hals: nt.hals, abschnitte: nt.abschnitte, maxY: Math.max(...nt.oy.map(Math.abs)), maxX: Math.max(...nt.ox.map(Math.abs)) })),
  };
}

/* Glanz mindern: Spiegelungen von Lampe und Fenster hängen an der
   Kamera und liegen in jedem Foto nahe der Mitte — also mitten in
   seinem eigenen Streifen der Textur, rundum so oft, wie es Fotos gibt.
   Die beiden Nachbarn sehen dieselbe Stelle um etwa 60° von der Seite,
   ohne diesen Glanz. Zwei Durchgänge auf einem Raster von 2 Pixeln:
   1. Kern: Ist das eigene Foto geglättet (σ 4 Pixel) deutlich heller als
      der dunklere Nachbar (nach dem Angleichen an den Nähten; ab dem
      1,6-fachen und 40 Stufen) und der Nachbar dort dunkel (unter 110:
      Glas, kein Etikett), ist es Glanz — es sei denn, ein Nachbar sieht
      die Stelle nicht streifend und hell (seitliches Licht macht nur
      einen Nachbarn dunkler, einen Glanz in der Mitte des eigenen Fotos
      sehen beide nicht). Geglättet verglichen, weil Schrift auf einem
      Etikett in allen Fotos im Mittel gleich hell aussieht, auch wenn
      sie um ein, zwei Pixel versetzt liegt.
   2. Hof: Schräg spiegelt Glas die helle Wand — die Nachbarn zeigen die
      Stelle grau, wo das eigene Foto ringsum dunkles Glas zeigt; und
      um einen Lampenglanz liegt im eigenen Foto ein heller Hof. Darum
      werden die Nachbarn je Seite auf die Umgebung des eigenen Fotos
      gebracht (Verhältnis der Helligkeit ringsum ohne den Kern, σ 54
      Pixel), und nahe am Kern (24 Pixel) gilt auch als Glanz, was
      danach noch deutlich heller ist (ab dem 1,4-fachen und 15 Stufen).
   Ersetzt wird durch den nach dem Angleichen dunkleren Nachbarn. Nicht
   an der Kapsel (oben bis `ab`, bis `voll` einsetzend): Eine Metall-
   kapsel glänzt in jedem Foto anders, ihre Schrift stand dort doppelt. */
function glanzMindern(daten, B, H, besitzer, links, rechts, farbeV, relBei, ab = 0, voll = 0, teil = null) {
  const S = 2;
  const bG = Math.ceil(B / S), hG = Math.ceil(H / S), nG = bG * hG;
  const gIndex = (y, c) => Math.min(hG - 1, Math.round(y / S)) * bG + Math.min(bG - 1, Math.round(c / S));
  const hell = (v) => 0.299 * v[0] + 0.587 * v[1] + 0.114 * v[2];
  /* Nachbarn eines Fotos mit ihrer Verstärkung an der gemeinsamen Naht */
  const nachbarn = (i) => {
    const ln = links.get(i), rn = rechts.get(i);
    return ln && rn ? [[ln.a, ln, 0.5], [rn.b, rn, -0.5]] : [];
  };
  /* je Seite (0 links, 1 rechts) Farbe und Sicht des Nachbarn — nicht
     über eine Lücke zwischen Teilen hinweg (siehe winkelWaehlen): Dort
     ist die Lage nur geschätzt, ein Etikett läge im Nachbarn versetzt,
     und das eigene Papier sähe neben seinem Glas wie Glanz aus */
  const nachbarSicht = (i, y, c, k) => {
    const nb = nachbarn(i)[k];
    if (!nb || nb[0] === i || (teil && teil[nb[0]] !== teil[i])) return null;
    const rel = relBei(nb[0], y, c);
    if (rel < 0.2) return null;
    const fj = farbeV(nb[0], y, c);
    if (!fj) return null;
    return { rel, farbe: [0, 1, 2].map((ch) => fj[ch] * Math.exp(nb[2] * nb[1].log[ch][y])) };
  };
  const eigenBei = (y, c) => { const o = y * B + c; return hell([daten[o * 4], daten[o * 4 + 1], daten[o * 4 + 2]]); };
  const daempf = (y) => Math.max(0, Math.min(1, (y - ab) / Math.max(1, voll - ab)));
  const stufe = (x, a, b) => Math.max(0, Math.min(1, (x - a) / (b - a)));

  /* 1. Kern */
  const eigen = new Float32Array(nG), anders = new Float32Array(nG), gegen = new Float32Array(nG);
  for (let gy = 0; gy < hG; gy++) {
    const y = Math.min(H - 1, gy * S);
    for (let gx = 0; gx < bG; gx++) {
      const c = Math.min(B - 1, gx * S), g = gy * bG + gx, i = besitzer[y * B + c];
      const lo = eigenBei(y, c);
      eigen[g] = lo; anders[g] = lo;
      if (i < 0 || lo <= 60 || y < ab) continue;
      for (let k = 0; k < 2; k++) {
        const n = nachbarSicht(i, y, c, k);
        if (!n) continue;
        const wh = hell(n.farbe);
        anders[g] = Math.min(anders[g], wh);
        if (n.rel >= 0.3) gegen[g] = Math.max(gegen[g], wh);
      }
    }
  }
  const eG = gauss(eigen, bG, hG, 4 / S), aG = gauss(anders, bG, hG, 4 / S), vG = gauss(gegen, bG, hG, 4 / S);
  const kern = new Float32Array(nG);
  for (let g = 0; g < nG; g++) {
    kern[g] = stufe(eG[g] / Math.max(1, aG[g]), 1.6, 2.4) * stufe(eG[g] - aG[g], 40, 80) * stufe(-aG[g], -110, -80)
      * stufe(0.75 * eG[g] - vG[g], 0, 0.2 * eG[g]);
  }
  /* Maximum über ±r Punkte, getrennt nach Zeilen (ringsum) und Spalten */
  const ausweiten = (feld, r) => {
    const zw = new Float32Array(nG), aus = new Float32Array(nG);
    for (let gy = 0; gy < hG; gy++) {
      const z = gy * bG;
      for (let gx = 0; gx < bG; gx++) {
        let m = 0;
        for (let dx = -r; dx <= r; dx++) { const v = feld[z + (gx + dx + bG) % bG]; if (v > m) m = v; }
        zw[z + gx] = m;
      }
    }
    for (let gy = 0; gy < hG; gy++) {
      const y0 = Math.max(0, gy - r), y1 = Math.min(hG - 1, gy + r);
      for (let gx = 0; gx < bG; gx++) {
        let m = 0;
        for (let yy = y0; yy <= y1; yy++) { const v = zw[yy * bG + gx]; if (v > m) m = v; }
        aus[gy * bG + gx] = m;
      }
    }
    return aus;
  };
  const kernWeit = ausweiten(kern, 3), nahe = ausweiten(kern, 12);
  let irgendwo = false;
  for (let g = 0; g < nG; g++) if (kern[g] > 0.05) { irgendwo = true; break; }
  if (!irgendwo) return;

  /* Umgebung je Seite (Raster 6 Pixel, ohne den Kern) */
  const S2 = 6, b2 = Math.ceil(B / S2), h2 = Math.ceil(H / S2), n2 = b2 * h2;
  const wO = new Float32Array(n2), sO = new Float32Array(n2);
  const wN = [new Float32Array(n2), new Float32Array(n2)], sN = [new Float32Array(n2), new Float32Array(n2)];
  for (let gy = 0; gy < h2; gy++) {
    const y = Math.min(H - 1, gy * S2);
    for (let gx = 0; gx < b2; gx++) {
      const c = Math.min(B - 1, gx * S2), g = gy * b2 + gx, i = besitzer[y * B + c];
      if (i < 0) continue;
      const w0 = Math.max(0, 1 - kernWeit[gIndex(y, c)]);
      wO[g] = w0; sO[g] = w0 * eigenBei(y, c);
      for (let k = 0; k < 2; k++) {
        const n = nachbarSicht(i, y, c, k);
        if (n) { wN[k][g] = w0; sN[k][g] = w0 * hell(n.farbe); }
      }
    }
  }
  const gl = (v) => gauss(v, b2, h2, 9);
  const F = { wO: gl(wO), sO: gl(sO), wN: wN.map(gl), sN: sN.map(gl) };
  const angleich = (k, y, c) => {
    const g = Math.min(h2 - 1, Math.round(y / S2)) * b2 + Math.min(b2 - 1, Math.round(c / S2));
    if (F.wO[g] < 0.02 || F.wN[k][g] < 0.02) return 1;
    return Math.max(0.1, Math.min(1.5, (F.sO[g] / F.wO[g] + 5) / (F.sN[k][g] / F.wN[k][g] + 5)));
  };
  /* der nach dem Angleichen dunklere Nachbar */
  const ersatz = (i, y, c) => {
    let w = null, wh = Infinity;
    for (let k = 0; k < 2; k++) {
      const n = nachbarSicht(i, y, c, k);
      if (!n) continue;
      const f = angleich(k, y, c), h = hell(n.farbe) * f;
      if (h < wh) { wh = h; w = n.farbe.map((x) => x * f); }
    }
    return w && { w, wh };
  };

  /* 2. Hof */
  const hof = new Float32Array(nG), eigen2 = new Float32Array(nG), anders2 = new Float32Array(nG);
  for (let gy = 0; gy < hG; gy++) {
    const y = Math.min(H - 1, gy * S);
    for (let gx = 0; gx < bG; gx++) {
      const g = gy * bG + gx, c = Math.min(B - 1, gx * S);
      eigen2[g] = eigen[g]; anders2[g] = eigen[g];
      if (nahe[g] < 0.05 || y < ab) continue;
      const i = besitzer[y * B + c];
      if (i < 0) continue;
      const e = ersatz(i, y, c);
      if (e) anders2[g] = Math.min(eigen[g], e.wh);
    }
  }
  const eG2 = gauss(eigen2, bG, hG, 4 / S), aG2 = gauss(anders2, bG, hG, 4 / S);
  for (let g = 0; g < nG; g++) {
    if (nahe[g] < 0.05) continue;
    hof[g] = stufe(eG2[g] / Math.max(1, aG2[g]), 1.4, 2) * stufe(eG2[g] - aG2[g], 15, 40) * stufe(-aG[g], -110, -80) * nahe[g];
  }
  const anteil = new Float32Array(nG);
  /* nur auf Glas: Zeigt der Nachbar in der Nähe (±16 Pixel) helles
     Papier, ist es ein Etikett — dessen Schrift liegt im Nachbarn um ein,
     zwei Pixel versetzt, und das Minimum beider wirkt dunkler als das
     eigene Foto, ohne dass dort Glanz wäre; und sein Rand liegt im
     Nachbarn oft etwas daneben (sonst bliebe ein dunkler Saum) */
  const papier = ausweiten(anders, 8);
  for (let g = 0; g < nG; g++) {
    anteil[g] = Math.max(kernWeit[g] * (eG2[g] - aG2[g] > 8 ? 1 : 0) * stufe(-aG[g], -110, -80), kern[g], hof[g]) * stufe(-papier[g], -160, -130);
  }
  const aW = gauss(anteil, bG, hG, 1);

  for (let y = 0; y < H; y++) {
    const dy = daempf(y);
    if (dy <= 0) continue;
    for (let c = 0; c < B; c++) {
      const a = Math.min(1, aW[gIndex(y, c)]) * dy;
      if (a < 0.02) continue;
      const o = y * B + c, i = besitzer[o];
      if (i < 0) continue;
      const e = ersatz(i, y, c);
      if (!e) continue;
      for (let ch = 0; ch < 3; ch++) daten[o * 4 + ch] += (e.w[ch] - daten[o * 4 + ch]) * a;
    }
  }
}

/* Textur so drehen, dass sie im ruhigsten Glas aufgeschnitten ist;
   die Vorderseite als Anteil der Breite */
function schneidenUndVorne(daten, B, H, vorneSpalte = null) {
  const ring = new Float64Array(B);
  for (let y = 0; y < H; y += 2) {
    for (let c = 0; c < B; c++) {
      const o = (y * B + c) * 4, p = (y * B + ((c + 1) % B)) * 4;
      ring[c] += Math.abs(daten[o] - daten[p]) + Math.abs(daten[o + 1] - daten[p + 1]) + Math.abs(daten[o + 2] - daten[p + 2]);
    }
  }
  const schnitt = ruhigsteSpalte(ring);
  const aus = new Uint8ClampedArray(daten.length);
  for (let y = 0; y < H; y++) {
    const z = y * B * 4;
    const teil = (B - schnitt) * 4;
    aus.set(daten.subarray(z + schnitt * 4, z + B * 4), z);
    aus.set(daten.subarray(z, z + schnitt * 4), z + teil);
  }
  /* Vorderseite: vorgegeben (Spalte vor dem Schnitt) oder das breiteste Etikett */
  const vorne = vorneSpalte != null ? (((vorneSpalte - schnitt) % B) + B) % B : vorderseite(aus, B, H, 0);
  return { daten: aus, schnitt, vorne: vorne / B };
}

/* Form für das 3D-Modell aus der gemessenen Gestalt (mm, Bauchradius
   wie die Grundform) */
function formAusGestalt(gestalt, rho, formSchluessel) {
  const basis = formVon(formSchluessel);
  const Rk = koerperRadius(basis);
  const hoehe = Math.round(gestalt.uBoden * Rk * 10) / 10;
  const punkte = [];
  const SCHRITTE = 64;
  for (let k = SCHRITTE; k >= 0; k--) {
    const a = k / SCHRITTE, h = hoehe * (1 - a);
    let r = Math.min(Rk, rho(a * gestalt.uBoden) * Rk);
    if (h < 9) {
      /* Boden wie bei der Grundform gerundet */
      const p = basis.profil;
      let ant = 1;
      for (let i = 1; i < p.length; i++) if (h <= p[i][0]) { ant = (p[i - 1][1] + (p[i][1] - p[i - 1][1]) * (h - p[i - 1][0]) / (p[i][0] - p[i - 1][0])) / Rk; break; }
      r = Rk * ant;
    }
    punkte.push([Math.round(h * 10) / 10, Math.round(r * 10) / 10]);
  }
  punkte[0][0] = 0; punkte[punkte.length - 1][0] = hoehe;
  return { name: basis.name, hoehe, kapsel: basis.kapsel, profil: punkte, gemessen: true };
}


/* Höhenversatz je Foto in der groben Abwicklung: Zeile im Foto a
   minus Zeile im Foto b ≈ (c_a + e_a·y) − (c_b + e_b·y), kleinste
   Quadrate über die passenden Paare (Foto 0 fest). Liefert je Foto
   y ↦ c + e·y oder null. */
function hoehenVersatz(n, paare, nr) {
  if (n < 2) return null;
  const m = 2 * (n - 1), A = Array.from({ length: m }, () => new Array(m).fill(0)), r = new Array(m).fill(0);
  const spalten = (i, y) => (i === 0 ? [] : [[2 * (i - 1), 1], [2 * (i - 1) + 1, y / nr]]);
  for (const p of paare) {
    for (const t of [0.2, 0.5, 0.8]) {
      const y = t * nr, z = [...spalten(p.a, y), ...spalten(p.b, y).map(([j, v]) => [j, -v])], ziel = p.dyBei(y);
      for (const [j, v] of z) { r[j] += v * ziel; for (const [k, w] of z) A[j][k] += v * w; }
    }
  }
  for (let j = 0; j < m; j++) A[j][j] += 1e-6;
  const x = loesen(A, r);
  if (!x) return null;
  return Array.from({ length: n }, (_, i) => (i === 0 ? () => 0 : (y) => x[2 * (i - 1)] + x[2 * (i - 1) + 1] * y / nr));
}


/* Boden aus der Unterkante: Unter dem Bauch läuft der Umriss oft
   gerade weiter (Schatten, Spiegelbild, Kanten auf der Abstellfläche),
   und wo er schmaler wird, liegt nicht immer der Boden. Verlässlicher
   ist vorn auf der Achse der Übergang von der Flasche zur
   Abstellfläche — der tiefste Punkt der Bodenkante, im Foto deutlich
   unter den Enden des seitlichen Umrisses. Je Foto werden entlang der
   Achse (ψ = 0) die deutlichen Stufen in Helligkeit und Buntheit
   gesucht (ein Streifen von ±0,3 Bauchradien, gemittelt); die
   tiefste ist der Boden (darüber liegen Etikettränder, darunter nur
   noch die Abstellfläche). Liefert { uBoden (Median), jeFoto } in
   Bauchradien oder null. */
function bodenVorn(lagen, rho, f, uBoden) {
  const DSB = 0.01, von = uBoden - 1.2, bis = uBoden + 0.3, nb = Math.round((bis - von) / DSB) + 1;
  const kandidaten = lagen.map((L) => {
    const hell = new Float32Array(nb).fill(NaN), bunt = new Float32Array(nb).fill(NaN);
    for (let i = 0; i < nb; i++) {
      const sv = von + i * DSB;
      const r = Math.min(rho(Math.min(sv, uBoden - 0.4)), 1) * L.kb;
      let sh = 0, sb = 0, z = 0;
      for (let k = -6; k <= 6; k++) {
        /* ±0,3 Bauchradien seitlich: ψ so, dass r·sin ψ = 0,3·k/6 · kb */
        const psi = Math.asin(Math.max(-0.95, Math.min(0.95, 0.3 * (k / 6) * L.kb / r)));
        const p = abbilden(L, rho, f, sv, psi);
        if (!p || !(p[0] >= 1 && p[1] >= 1 && p[0] < L.W - 2 && p[1] < L.H - 2)) continue;
        const o = (Math.round(p[1]) * L.W + Math.round(p[0])) * 4, d = L.data;
        sh += L.g[Math.round(p[1]) * L.W + Math.round(p[0])];
        sb += Math.max(d[o], d[o + 1], d[o + 2]) - Math.min(d[o], d[o + 1], d[o + 2]);
        z++;
      }
      if (z >= 7) { hell[i] = sh / z; bunt[i] = sb / z; }
    }
    const stufe = new Float32Array(nb);
    let max = 0;
    for (let i = 3; i < nb - 3; i++) {
      const dh = (hell[i + 1] + hell[i + 2] + hell[i + 3]) - (hell[i - 1] + hell[i - 2] + hell[i - 3]);
      const db = (bunt[i + 1] + bunt[i + 2] + bunt[i + 3]) - (bunt[i - 1] + bunt[i - 2] + bunt[i - 3]);
      const v = Math.max(Math.abs(dh), 0.8 * Math.abs(db)) / 3;
      stufe[i] = Number.isFinite(v) ? v : 0;
      max = Math.max(max, stufe[i]);
    }
    let tiefste = NaN;
    for (let i = 4; i < nb - 4; i++) {
      if (stufe[i] >= Math.max(12, 0.35 * max) && stufe[i] >= stufe[i - 1] && stufe[i] > stufe[i + 1]) tiefste = von + i * DSB;
    }
    return tiefste;
  });
  const gut = kandidaten.filter(Number.isFinite);
  if (gut.length < Math.max(2, Math.ceil(lagen.length / 2))) return null;
  const mitte = median(gut);
  /* Ausreißer (Schatten vor der Flasche) zählen nicht. Die Stufe liegt
     mitten im Übergang; ein Hauch darüber (gut 1 mm) bleibt sicher auf
     der Flasche. */
  const RAND = 0.03;
  return { uBoden: mitte - RAND, jeFoto: kandidaten.map((v) => (Number.isFinite(v) && Math.abs(v - mitte) < 0.25 ? v : mitte) - RAND), roh: kandidaten };
}

/* Teile ohne gemeinsame Stellen (siehe winkelWaehlen) liegen in der
   Höhe nur so genau wie ihre Oberkanten (etwa 0,2 Bauchradien, siehe
   7c). Die Bodenkante vorn ist genauer: Je Teil der Median der
   Bodenkanten seiner Fotos, verschoben auf den des größten Teils.
   Liefert je Teil die Verschiebung in Bauchradien. */
function teileVersatz(teil, boeden) {
  const nt = Math.max(...teil) + 1;
  const werte = Array.from({ length: nt }, (_, t) => boeden.filter((v, i) => teil[i] === t && Number.isFinite(v)));
  let ref = 0;
  for (let t = 1; t < nt; t++) if (werte[t].length > werte[ref].length) ref = t;
  if (!werte[ref].length) return new Array(nt).fill(0);
  const ziel = median(werte[ref]);
  return werte.map((w) => (w.length ? ziel - median(w) : 0));
}


/* ── Ablauf ────────────────────────────────────────────────── */

/** Rundum-Etikett aus Fotos. `fotos`: [{ data, width, height, exif? }],
    am besten 6 im Abstand von etwa 60°, die Flasche ganz im Bild.
    Liefert dasselbe wie abwicklungAuswerten ({ breite, hoehe, daten,
    vorne, form, abdeckung, … }) oder { fehler, foto? }. */
async function fotoserieAuswerten(fotosRoh, formSchluessel, { pause = warte, beiFortschritt = () => {}, protokoll = () => {} } = {}) {
  if (!fotosRoh || fotosRoh.length < 3) return { fehler: "zuWenige" };
  const n = fotosRoh.length;
  /* 1. je Foto die Flasche suchen — erst die Symmetriesuche (ohne
     Annahme über Lage, Größe und Form), sonst die Erkennung aus dem
     Video an mehreren Stellen (siehe messen) */
  const bilder = [];
  for (let i = 0; i < n; i++) {
    const bild = verkleinern(fotosRoh[i], ARBEITS_KANTE);
    bilder.push({ bild, lum: luminanz(bild), sym: flascheSymmetrisch(bild), f0: brennweitePixel(fotosRoh[i].exif, bild.width, bild.height) });
    beiFortschritt(0.12 * (i + 1) / n); await pause();
  }
  /* 2.–4. Umriss und Gestalt nach einer Grundform */
  const messen = async (vorlage, von, bis) => {
    const fotos = [];
    for (let i = 0; i < n; i++) {
      const { bild, lum, sym, f0 } = bilder[i];
      let gefunden = sym, m = gefunden && gestaltMessen(bild, lum, gefunden.lage, vorlage, gefunden.bauch);
      if (!m) { gefunden = flascheFinden(bild, vorlage); m = gefunden && gestaltMessen(bild, lum, gefunden.lage, vorlage); }
      if (!m) return { fehler: "flascheFehlt", foto: i };
      fotos.push({ ...m, f0, data: bild.data, g: lum.g });
      beiFortschritt(von + (bis - von) * (i + 1) / n); await pause();
    }
    const f0 = median(fotos.map((m) => m.f0));
    const gestalten = fotos.map((m) => gestaltRechnen(m, f0));
    const fehlt = gestalten.findIndex((g) => !g);
    if (fehlt >= 0) return { fehler: "flascheFehlt", foto: fehlt };
    return { fotos, f0, gestalten };
  };
  let schluessel = formSchluessel, vorlage = profilAusForm(schluessel);
  let gemessen = await messen(vorlage, 0.12, 0.25);
  /* scheitert der Umriss an der gewählten Grundform, die anderen */
  for (const k of FORM_REIHE) {
    if (!gemessen.fehler || typeof formSchluessel !== "string") break;
    if (k === formSchluessel) continue;
    const v = await messen(profilAusForm(k), 0.12, 0.25);
    if (!v.fehler) { gemessen = v; schluessel = k; vorlage = profilAusForm(k); }
  }
  if (gemessen.fehler) return gemessen;
  /* Passt die gewählte Grundform nicht zur gemessenen Höhe (Riesling
     in der Bordeauxflasche gewählt, aber eine Schlegelflasche
     fotografiert), noch einmal mit der passenden */
  const kuerzeste = Math.min(...FORM_REIHE.map((k) => profilAusForm(k).uBoden));
  const stufenGemessen = stufenVon(gemessen.gestalten);
  const uGemessen = median(bodenJeFoto(stufenGemessen, 0.75 * kuerzeste).filter(Number.isFinite));
  const besser = passendeForm(schluessel, uGemessen, stufenGemessen);
  protokoll("form", { gewaehlt: schluessel, uGemessen, besser });
  if (besser !== schluessel) {
    const zweite = await messen(profilAusForm(besser), 0.25, 0.3);
    if (!zweite.fehler) { gemessen = zweite; schluessel = besser; vorlage = profilAusForm(besser); }
  }
  const { fotos, f0, gestalten } = gemessen;
  const gestalt = gemeinsameGestalt(gestalten, vorlage);
  const rho = rhoVon(gestalt);
  fotos.forEach((m, i) => Object.assign(m, gestalten[i]));
  protokoll("gestalt", { uBoden: gestalt.uBoden, kb: gestalten.map((g) => g.kb), f0 });

  /* 6. Abgleich aller Paare */
  const abw = fotos.map((m) => grobAbwickeln(m, rho, f0, gestalt.uBoden));
  const pkt = abw.map(merkmale);
  beiFortschritt(0.35); await pause();
  const kanten = [];
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      for (const g of paarAbgleich(pkt[a], pkt[b])) kanten.push({ a, b, ...g, dphi: g.dx / ABGLEICH_R });
    }
    await pause();
  }
  protokoll("paare", kanten.map(({ a, b, dx, dy, paare: p, gewicht }) => ({ a, b, dx, dy, anzahl: p.length, gewicht })));
  const gewaehlt = winkelWaehlen(n, kanten);
  if (!gewaehlt) {
    const verbunden = new Set(kanten.flatMap((k) => [k.a, k.b]));
    const allein = [...Array(n).keys()].find((i) => !verbunden.has(i));
    return allein != null ? { fehler: "keineUeberlappung", foto: allein } : { fehler: "luecke" };
  }
  /* teil: zu welchem Teil ein Foto gehört — Fotos verschiedener Teile
     haben nichts Gemeinsames gezeigt (blankes Glas) und werden auch
     nicht dicht verglichen; ihre Lage zueinander bleibt, wie gesetzt */
  const { phi, mass, teil } = gewaehlt;
  const getrennt = (a, b) => teil[a] !== teil[b];
  protokoll("winkel", gewaehlt);
  /* je Paar die stärkste passende Gruppe */
  const paare = [];
  for (const p of kanten) {
    if (Math.abs(wickeln(phi[p.b] - phi[p.a] - mass * p.dphi)) > 0.1) continue;
    const da = paare.findIndex((q) => q.a === p.a && q.b === p.b);
    if (da < 0) paare.push(p); else if (p.gewicht > paare[da].gewicht) paare[da] = p;
  }
  /* Höhenversatz der passenden Paare als Gerade über die Höhe (die
     Maßstäbe der Fotos sind vor dem Ausgleich nur auf ein, zwei
     Prozent gleich) */
  const dyVon = (p) => {
    const ys = p.paare.map(([ia]) => pkt[p.a][ia].y), dys = p.paare.map(([ia, ib]) => pkt[p.a][ia].y - pkt[p.b][ib].y);
    const g = ys.length >= 6 && Math.max(...ys) - Math.min(...ys) > 0.15 * abw[p.a].nr ? robusteGerade(ys, dys, 3, 0.5) : null;
    return g ? (y) => g.a + g.b * y : () => p.dy;
  };
  for (const p of paare) p.dyBei = dyVon(p);
  /* Paare, die sich überlappen müssten, aber keine passende Gruppe
     haben (zu wenige markante Punkte, oder eine Scheingruppe war
     stärker): Versatz aus den Gesamtwinkeln und je Foto einem
     Höhenversatz c + e·y, der zu den passenden Paaren passt. Ohne
     sie bliebe die Runde um die Flasche offen. */
  const hoehe = hoehenVersatz(n, paare, abw[0].nr);
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      const d = wickeln(phi[b] - phi[a]);
      if (!hoehe || Math.abs(d) > 1.9 || getrennt(a, b) || paare.some((p) => p.a === a && p.b === b)) continue;
      /* eine gemessene Gruppe in der Nähe der Vorhersage geht vor */
      const nah = kanten.filter((p) => p.a === a && p.b === b && Math.abs(wickeln(d - mass * p.dphi)) < 0.25)
        .sort((p, q) => q.gewicht - p.gewicht)[0];
      const dyBei = (y) => hoehe[a](y) - hoehe[b](y);
      paare.push(nah ? { a, b, dx: nah.dx, dyBei: dyVon(nah), weit: false } : { a, b, dx: d * ABGLEICH_R / mass, dyBei, weit: true });
    }
  }
  /* Treffer der Paare in Fotopixeln */
  const zuPixel = (m, ab, x, y) => abbilden(m, rho, f0, y / ABGLEICH_R, (x - (ab.nc - 1) / 2) / ABGLEICH_R);
  const messungen = [];
  for (const p of paare) {
    const tr = dichterAbgleich(abw[p.a], abw[p.b], pkt[p.a], pkt[p.b], p.dx, p.dyBei, p.weit ? 12 : 5, p.weit ? 18 : 9);
    protokoll("dicht", { a: p.a, b: p.b, dx: p.dx, weit: !!p.weit, dy: [0.2, 0.5, 0.8].map((t) => p.dyBei(t * abw[0].nr)), n: tr.length, dxMed: median(tr.map((t) => t[0] - t[2])) });
    for (const [xa, ya, xb, yb] of tr) {
      /* nicht an der Schulter: Dort ist die Gestalt nur angenähert */
      const sa = ya / ABGLEICH_R;
      if (gestalt.schulter && sa > gestalt.schulter[0] - 0.1 && sa < gestalt.schulter[1] + 0.1) continue;
      const qa = zuPixel(fotos[p.a], abw[p.a], xa, ya), qb = zuPixel(fotos[p.b], abw[p.b], xb, yb);
      if (qa && qb) messungen.push([p.a, qa[0], qa[1], p.b, qb[0], qb[1]]);
    }
    await pause();
  }
  beiFortschritt(0.5); await pause();

  /* 7. Ausgleich */
  /* Brennweite: aus EXIF genau bekannt (eng), sonst angenommen (weiter) */
  const mitExif = fotosRoh.every((b) => b.exif && b.exif.kleinbild);
  const fStreuung = mitExif ? 0.01 : 0.03;
  let geo = ausgleichen(fotos, messungen, rho, { f: f0, fStreuung, kb: fotos.map((m) => m.kb), ht: fotos.map((m) => m.htop), phi: phi.map((v) => v - phi[0]) });
  protokoll("ausgleich", { f: geo.f, kb: geo.kb, ht: geo.ht, phi: geo.phi, kipp: geo.kipp, restMedian: geo.restMedian, restQ90: geo.restQ90, genutzt: geo.genutzt, messungen: messungen.length });
  beiFortschritt(0.55); await pause();

  /* 7b. Zweite Runde: Mit der ausgeglichenen Lage liegen die groben
     Abwicklungen fast deckungsgleich übereinander — auch an Hals und
     Kapsel, wo die erste Runde kaum Treffer fand (der Höhenversatz der
     Paare war dort nur aus dem Bauch hochgerechnet, und ohne Treffer
     richtete der Ausgleich die Kapsel nicht aus). Alle Nachbarn werden
     noch einmal dicht nachgemessen, dann wird neu ausgeglichen. */
  const lageVon = (g) => fotos.map((m, i) => ({ ...m, kb: g.kb[i], htop: g.ht[i], ...homographien(g.f, m.cx, m.cy, rektifizierung(g.f, m.cx, m.cy, m.flucht, m.achsPunkt, g.kipp[i])) }));
  {
    const lagen = lageVon(geo);
    const abw2 = lagen.map((m) => grobAbwickeln(m, rho, geo.f, gestalt.uBoden));
    const nrm2 = abw2.map(normiert);
    const messungen2 = [];
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const d = wickeln(geo.phi[b] - geo.phi[a]);
        if (Math.abs(d) > 1.9 || getrennt(a, b)) continue;
        /* nach dem Ausgleich liegen die Abwicklungen fast aufeinander:
           engeres Suchfenster */
        for (const [xa, ya, xb, yb] of dichterAbgleich(abw2[a], abw2[b], nrm2[a], nrm2[b], d * ABGLEICH_R, () => 0, 4, 5, 7)) {
          const sa = ya / ABGLEICH_R;
          if (gestalt.schulter && sa > gestalt.schulter[0] - 0.1 && sa < gestalt.schulter[1] + 0.1) continue;
          const qa = abbilden(lagen[a], rho, geo.f, sa, (xa - (abw2[a].nc - 1) / 2) / ABGLEICH_R);
          const qb = abbilden(lagen[b], rho, geo.f, yb / ABGLEICH_R, (xb - (abw2[b].nc - 1) / 2) / ABGLEICH_R);
          if (qa && qb) messungen2.push([a, qa[0], qa[1], b, qb[0], qb[1]]);
        }
        await pause();
      }
    }
    /* Über die Lücken zwischen Teilen (siehe winkelWaehlen): Die Lage
       dort ist nur geschätzt, auf einige Grad. Wo beide Fotos dennoch
       dasselbe zeigen (Schrift rundum auf der Kapsel), wird in einem
       weiten Fenster gesucht; es zählen nur Treffer, die sich in der
       Verschiebung einig sind, mindestens sechs — auf blankem Glas
       findet sich nichts. Dann richten sie die Teile zueinander aus. */
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const d = wickeln(geo.phi[b] - geo.phi[a]);
        if (Math.abs(d) > 1.9 || !getrennt(a, b)) continue;
        const tr = dichterAbgleich(abw2[a], abw2[b], nrm2[a], nrm2[b], d * ABGLEICH_R, () => 0, 22, 30, 7);
        if (tr.length < 6) continue;
        const mx = median(tr.map((t) => t[0] - t[2])), my = median(tr.map((t) => t[1] - t[3]));
        const einig = tr.filter((t) => Math.abs(t[0] - t[2] - mx) < 2 && Math.abs(t[1] - t[3] - my) < 3);
        protokoll("bruecke", { a, b, n: tr.length, einig: einig.length, dx: mx - d * ABGLEICH_R, dy: my });
        if (einig.length < 6 || einig.length < 0.6 * tr.length) continue;
        for (const [xa, ya, xb, yb] of einig) {
          const qa = abbilden(lagen[a], rho, geo.f, ya / ABGLEICH_R, (xa - (abw2[a].nc - 1) / 2) / ABGLEICH_R);
          const qb = abbilden(lagen[b], rho, geo.f, yb / ABGLEICH_R, (xb - (abw2[b].nc - 1) / 2) / ABGLEICH_R);
          if (qa && qb) messungen2.push([a, qa[0], qa[1], b, qb[0], qb[1]]);
        }
        /* die beiden Teile sind nun einer */
        const alt = teil[b], neu = teil[a];
        for (let i = 0; i < n; i++) if (teil[i] === alt) teil[i] = neu;
        await pause();
      }
    }
    if (messungen2.length >= 0.5 * messungen.length) {
      geo = ausgleichen(fotos, messungen2, rho, { f: geo.f, fVorgabe: f0, fStreuung, kb: geo.kb, ht: geo.ht, phi: geo.phi, kipp: geo.kipp });
      protokoll("ausgleich2", { f: geo.f, kb: geo.kb, ht: geo.ht, phi: geo.phi, kipp: geo.kipp, restMedian: geo.restMedian, restQ90: geo.restQ90, genutzt: geo.genutzt, messungen: messungen2.length });
    }
  }
  beiFortschritt(0.6); await pause();

  /* 7c. Höhe: Die Treffer legen nur fest, wie die Fotos zueinander
     liegen, nicht wie hoch alle zusammen — das Profil tut es kaum, wenn
     am Hals nichts zu sehen ist (blankes Glas, keine Kapsel), und dann
     läuft die Höhe im Ausgleich mit der Kippung davon. Die Oberkante
     selbst ist nur auf etwa 0,2 Bauchradien sicher (vordere oder
     hintere Lippe, Kapseldeckel von oben). Liegt sie (Median der Fotos
     eines Teils) danach mehr als 0,1 Bauchradien unter s = 0, zeigte
     die Textur oben einen Streifen Hintergrund; liegt sie mehr als
     0,25 darüber, fehlte oben ein Stück. In beiden Fällen wird der Teil
     zurückgeschoben. */
  {
    const lg = lageVon(geo);
    const sOben = lg.map((L) => rueckabbilden(L, rho, geo.f, L, L.kb, L.htop, 0, L.obenPunkt[0], L.obenPunkt[1])[0]);
    const versatz = Array.from({ length: Math.max(...teil) + 1 }, (_, t) => {
      const w = sOben.filter((v, i) => teil[i] === t && Number.isFinite(v));
      const m = w.length ? median(w) : 0;
      return m > 0.1 || m < -0.25 ? m : 0;
    });
    protokoll("oben", { sOben, versatz });
    if (versatz.some((v) => v !== 0)) geo = { ...geo, ht: geo.ht.map((h, i) => h + versatz[teil[i]] * geo.kb[i]) };
  }

  /* 7d. Boden aus der Unterkante vorn (siehe bodenVorn); Teile ohne
     gemeinsame Stellen danach in der Höhe zueinander (teileVersatz) */
  let boden = bodenVorn(lageVon(geo), rho, geo.f, gestalt.uBoden);
  if (boden && Math.max(...teil) > 0) {
    const versatz = teileVersatz(teil, boden.roh);
    protokoll("teile", { teil, versatz });
    if (versatz.some((v) => Math.abs(v) > 0.01)) {
      geo = { ...geo, ht: geo.ht.map((h, i) => h - versatz[teil[i]] * geo.kb[i]) };
      boden = bodenVorn(lageVon(geo), rho, geo.f, gestalt.uBoden);
    }
  }
  protokoll("boden", { vorher: gestalt.uBoden, nachher: boden });
  if (boden) gestalt.uBoden = boden.uBoden;

  /* 8. Textur */
  const tex = textur(fotos, geo, rho, gestalt.uBoden, {
    boeden: boden && boden.jeFoto, sHals: gestalt.schulter ? gestalt.schulter[0] : 2, teil,
    beiZeile: (a) => beiFortschritt(0.6 + 0.35 * a),
  });
  protokoll("naehte", { kappa: tex.kappa, hals: tex.halsFest, naehte: tex.ausgleich });
  /* der Halsradius gilt auch für das Modell */
  if (tex.kappa !== 1 && gestalt.schulter) {
    const sH = gestalt.schulter[0];
    for (let b = 0; b < gestalt.rho.length; b++) {
      const sv = b * DS;
      gestalt.rho[b] *= sv <= sH ? tex.kappa : sv >= sH + 0.5 ? 1 : tex.kappa + (1 - tex.kappa) * (sv - sH) / 0.5;
    }
  }
  /* Vorderseite: die Mitte des ersten Fotos (Winkel 0, Spalte 0) */
  const { daten, vorne, schnitt } = schneidenUndVorne(tex.daten, tex.B, tex.H, 0);
  const form = formAusGestalt(gestalt, rho, schluessel);
  beiFortschritt(1);
  return {
    breite: tex.B, hoehe: tex.H, daten, vorne, form, profil: form,
    abdeckung: tex.abdeckung, fotos: n, schnitt, formSchluessel: schluessel,
    winkel: geo.phi.map((v) => (((v * 180 / Math.PI) % 360) + 360) % 360),
    brennweite: geo.f, rest: { median: geo.restMedian, q90: geo.restQ90, genutzt: geo.genutzt },
  };
}


/* Einzelschritte für Prüfstände (Fehlersuche an echten Fotos) */
const intern = { winkelWaehlen, kantenbild, flascheFinden, flascheSymmetrisch, gestaltMessen, gestaltRechnen, gemeinsameGestalt, rhoVon, grobAbwickeln, merkmale, paarAbgleich, abbilden, ausgleichen, textur, umrissMessen };

export { fotoserieAuswerten, intern, verkleinern };
